import { CadenciaBridge } from './CadenciaBridge';
import { hasNativePortableText } from '../public-format/native-text-contract';
import { rasterizeSmartObjectInSession } from '../render/rasterize-smart-object';
import type { ImageSourceEdit } from '../public-format/image-source-edit';
import { captureLayerClipboard, duplicateLayerInSession, pasteLayerClipboard, type LayerClipboard } from '../public-format/layer-clipboard';
import type { CommandExecution } from '../core/commands';
import { exportPublicScene } from '../io/export-public-scene';
import { lazy, Suspense, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRoving } from '../../shell/hooks';
import {
  AppWindow,
  Blend,
  Brush,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Command,
  Download,
  DownloadCloud,
  Eraser,
  FileImage,
  FolderOpen,
  Hand,
  HardDrive,
  Info,
  Layers3,
  Link2,
  Moon,
  Move,
  Palette,
  PenTool,
  Redo2,
  RotateCcw,
  RotateCw,
  Rows3,
  Save,
  Search,
  Scissors,
  SquareDashedMousePointer,
  Shapes,
  Sun,
  Type,
  Undo2,
  Upload,
  Unlink2,
  X,
  ZoomIn,
} from 'lucide-react';
import {
  AddLayerCommand,
  brushTipCoverage,
  ClearRasterSelectionCommand,
  ContentAwareFillCommand,
  FillRasterCommand,
  HistoryManager,
  HealSelectionCommand,
  SelectionCommand,
  TransformLayerCommand,
  isMatrixTransformCommand,
} from '../core/commands';
import { DocumentSession } from '../core/session';
import { IDENTITY_MATRIX, invertMatrix, matrixAround, multiplyMatrix, transformPoint, type Matrix, type Point } from '../core/geometry';
import { createAdjustmentLayer, createDocument, createId, createRasterLayer, findLayer, estimateDocumentBytes, layerBounds, layerDocumentTransform, layerParentTransform, walkLayers, type EditorDocument, type ImportReport, type TextLayer } from '../core/model';
import { DEFAULT_BLACK_WHITE } from '../filters/tone';
import { RasterTileStore } from '../core/tiles';
import { hasSelection, selectionBounds, type SelectionCombineMode, type SelectionState } from '../core/selection';
import { createDefaultCodecRegistry } from '../codecs/registry';
import { alphaChannels } from '../core/channels';
import { artboards, slices, type ExportAsset, type Slice } from '../core/export-resources';
import { exportFlattened, exportFlattenedZip, saveMaipoProject } from '../io/files';
import { exportArtboardArchive, exportAssetArchive, exportSliceArchive } from '../io/export-assets';
import { installFontFiles, loadStoredFonts } from '../io/fonts';
import { isInstallAvailable, isStandalone, promptInstall, subscribeInstallable } from '../io/pwa-install';
import { deleteRecovery, listRecoveries, loadRecovery, saveRecovery, saveRecoveryCheckpoint, type RecoverySummary } from '../persistence/recovery';
import { parsePairingFragment } from '@maipo/agent-protocol';
import { registerAgentHost, startAgentAccess, updateAgentState } from '../agent/agent-store';
import { AgentAccessPanel, AgentDialogs } from './AgentAccessPanel';
import { deleteTextDraftRecovery, listTextDraftRecoveries, loadTextDraftRecovery, type TextDraftRecoverySummary } from '../persistence/text-draft-recovery';
import { watchRecoveryAutosave } from '../persistence/autosave';
import { prepareRecoveryReload } from '../persistence/recovery-reload';
import { Canvas2DRenderer, type RenderStats, type Viewport } from '../render/renderer';
import { createExtensionScaleCommand, createExtensionTransformCommand, getExtensionObject } from '../render/extension-interactions';
import { analysisDiagnostics } from '../workers/analysis-client';
import { CanvasWorkspace } from './CanvasWorkspace';
import { ChannelsPanel, type ChannelView } from './ChannelsPanel';
import { ExportPanel } from './ExportPanel';
import type { GeometryDialogMode } from './GeometryDialog';
import { NotesPanel } from './NotesPanel';
import { FillPanel } from './FillPanel';
import { HistogramPanel } from './HistogramPanel';
import { LayerCompsPanel } from './LayerCompsPanel';
import { GlobalSwatchesPanel } from './GlobalSwatchesPanel';
import type { ToolShortcutMap } from './ShortcutDialog';
import type { DeformDialogMode } from './DeformDialog';
import { activePerspectivePlane, perspectivePlanes } from '../core/perspective';
import { listAddons, subscribeToAddons } from '../addons/registry';
import { AddonPanelHost, createAddonContext } from './AddonPanelHost';
import { LayerPanel } from './LayerPanel';
import { removeLayer, runPanelOperation } from './layer-intents';
import { actorDisplayName, USER_ACTOR, withoutActorPrefix } from './attribution';
import { offersRevert, revertTransaction } from '../semantic/revert';
import type { TransactionCommand } from '../semantic/transaction';
import { runCommand } from '../semantic/commands/run';

declare const __MAIPO_TEST_HOOKS__: boolean;
declare const __MAIPO_AGENT_API__: boolean;
import { CloudWorkspace } from '../../cloud/CloudWorkspace';
import { createEditorHost } from './editor-host';
import { SettingsSection } from './SettingsSection';
const EditorShell = lazy(() => import('../../shell/EditorShell').then((module) => ({ default: module.EditorShell })));

// Specialist dialogs are only mounted while open, so they load on demand and
// stay out of the initial bundle.
const PortableTextCanvasEditor = lazy(() => import('../vector/PortableTextCanvasEditor'));
const CreatePortableTextDialog = lazy(() => import('../vector/CreatePortableTextDialog'));
const GeometryDialog = lazy(() => import('./GeometryDialog').then((module) => ({ default: module.GeometryDialog })));
const GuideDialog = lazy(() => import('./GuideDialog').then((module) => ({ default: module.GuideDialog })));
const GuideLayoutDialog = lazy(() => import('./GuideLayoutDialog').then((module) => ({ default: module.GuideLayoutDialog })));
const ToolPresetsDialog = lazy(() => import('./ToolPresetsDialog').then((module) => ({ default: module.ToolPresetsDialog })));
const ShortcutDialog = lazy(() => import('./ShortcutDialog').then((module) => ({ default: module.ShortcutDialog })));
const RefineSelectionDialog = lazy(() => import('./RefineSelectionDialog').then((module) => ({ default: module.RefineSelectionDialog })));
const DeformDialog = lazy(() => import('./DeformDialog').then((module) => ({ default: module.DeformDialog })));
const ApplyImageDialog = lazy(() => import('./ApplyImageDialog').then((module) => ({ default: module.ApplyImageDialog })));
const ColorRangeDialog = lazy(() => import('./ColorRangeDialog').then((module) => ({ default: module.ColorRangeDialog })));
const VanishingPointDialog = lazy(() => import('./VanishingPointDialog').then((module) => ({ default: module.VanishingPointDialog })));
const BatchProcessDialog = lazy(() => import('./BatchProcessDialog').then((module) => ({ default: module.BatchProcessDialog })));
const DataDrivenDialog = lazy(() => import('./DataDrivenDialog').then((module) => ({ default: module.DataDrivenDialog })));
const HighPassDialog = lazy(() => import('./HighPassDialog').then((module) => ({ default: module.HighPassDialog })));
const DisplaceDialog = lazy(() => import('./DisplaceDialog').then((module) => ({ default: module.DisplaceDialog })));
const MatchColorDialog = lazy(() => import('./MatchColorDialog').then((module) => ({ default: module.MatchColorDialog })));
const LuminosityMasksDialog = lazy(() => import('./LuminosityMasksDialog').then((module) => ({ default: module.LuminosityMasksDialog })));
const ContentAwareScaleDialog = lazy(() => import('./ContentAwareScaleDialog').then((module) => ({ default: module.ContentAwareScaleDialog })));
const ColourTraceDialog = lazy(() => import('./ColourTraceDialog').then((module) => ({ default: module.ColourTraceDialog })));

import { editingContext, toolUnavailableReason, type EditingContext } from './editing-context';
import type { EditorSession, PointerReadout, ToolId, VectorObjectSelection } from './types';
import { growSelection, invertObjectSelection, sameFill, selectAllObjects, selectTouching, withSwatch } from '../vector/select-ops';
import { readTraceTopology } from '../vector/trace-topology';
import { expandToGroups, hasGroupedObject, TagObjectGroupCommand } from '../vector/object-groups';
import { ConvertToSmartObjectCommand, isSmartObject } from '../core/smart-objects';
import { AddActionCommand, documentActions, newRecordedAction, replayAction, replayActionBatch } from '../core/actions';
import type { ActionStep } from '../core/action-types';
import { brushPresets, createBrushPreset, sampleBrushTip, SetBrushPresetsCommand } from '../core/brush-presets';
import { documentNotes } from '../core/notes';
import { createToolPreset, SetToolPresetsCommand, toolPresets, type GridToolSettings, type ShapeToolKind, type ToolPreset, type ToolPresetSettings } from '../core/tool-presets';
import type { ExposureRange } from '../core/exposure-tools';
import { transformForPlacement, vectorDrawPlacement, type VectorDrawMode } from '../core/vector-draw-mode';
import type { IsolationScope } from './isolation';
import {
  EDITOR_ACTION_MEDIA, EDITOR_PANEL_MEDIA, MEDIA_MODES, NEUTRAL, RASTER, VECTOR,
  actionStepMedia, modeLabel, offeredIn, planModeSwitch, storeMediaMode, storedMediaMode,
  visibleIn, type EditorActionId, type EditorPanelId, type MediaMode, type MediaPolicy,
} from '../core/media-mode';

/**
 * Every tool declares which medium it works on, so the rail, the shortcuts and
 * anything else that filters read one catalog rather than each keeping its own
 * idea of what is a "vector tool". See ADR-004 and core/media-mode.ts.
 */
const TOOL_GROUPS = ['Navigate', 'Select', 'Paint', 'Retouch', 'Vector', 'Annotate'] as const;
type ToolGroup = (typeof TOOL_GROUPS)[number];

const tools: { id: ToolId; label: string; icon: typeof Move; media: MediaPolicy; group: ToolGroup }[] = [
  { id: 'move', label: 'Move', icon: Move, media: NEUTRAL, group: 'Navigate' },
  { id: 'marquee', label: 'Rectangle select', icon: SquareDashedMousePointer, media: RASTER, group: 'Select' },
  { id: 'quick-selection', label: 'Quick Selection', icon: CircleDot, media: RASTER, group: 'Select' },
  { id: 'note', label: 'Note', icon: Info, media: NEUTRAL, group: 'Annotate' },
  { id: 'shape', label: 'Shape', icon: Shapes, media: VECTOR, group: 'Vector' },
  { id: 'pen', label: 'Pen', icon: PenTool, media: VECTOR, group: 'Vector' },
  { id: 'shaper', label: 'Shaper', icon: Shapes, media: VECTOR, group: 'Vector' },
  { id: 'knife', label: 'Knife', icon: Scissors, media: VECTOR, group: 'Vector' },
  { id: 'brush', label: 'Brush', icon: Brush, media: RASTER, group: 'Paint' },
  { id: 'history-brush', label: 'History Brush', icon: Brush, media: RASTER, group: 'Paint' },
  { id: 'clone-stamp', label: 'Clone Stamp', icon: Brush, media: RASTER, group: 'Retouch' },
  { id: 'healing-brush', label: 'Healing Brush', icon: Brush, media: RASTER, group: 'Retouch' },
  { id: 'dodge', label: 'Dodge', icon: Sun, media: RASTER, group: 'Retouch' },
  { id: 'burn', label: 'Burn', icon: Moon, media: RASTER, group: 'Retouch' },
  { id: 'eraser', label: 'Eraser', icon: Eraser, media: RASTER, group: 'Paint' },
  { id: 'gradient', label: 'Gradient', icon: Blend, media: RASTER, group: 'Paint' },
  { id: 'hand', label: 'Hand', icon: Hand, media: NEUTRAL, group: 'Navigate' },
  { id: 'zoom', label: 'Zoom', icon: ZoomIn, media: NEUTRAL, group: 'Navigate' },
];

/**
 * Dock panels, grouped into families.
 *
 * Nine tabs competed for 320px at 9px type with labels colliding. Grouping them
 * means a family shows three or four at a readable size, and the All-panels
 * browser covers everything at once — including panels a media workspace hides.
 */
const PANEL_FAMILIES = [
  { id: 'build', label: 'Build', title: 'Build — Layers and Fill' },
  { id: 'inspect', label: 'Inspect', title: 'Inspect — Info and Analyze' },
  { id: 'flow', label: 'Flow', title: 'Workflow — History, Actions, Notes, Channels' },
  { id: 'output', label: 'Output', title: 'Output — Export and Agent access' },
  { id: 'ext', label: 'Ext', title: 'Extensions — panels contributed by add-ons' },
] as const;
type PanelFamily = (typeof PANEL_FAMILIES)[number]['id'];

const DOCK_PANELS: { id: EditorPanelId; label: string; family: PanelFamily; media: MediaPolicy }[] = [
  { id: 'layers', label: 'Layers', family: 'build', media: EDITOR_PANEL_MEDIA.layers },
  { id: 'fill', label: 'Fill', family: 'build', media: EDITOR_PANEL_MEDIA.fill },
  { id: 'document', label: 'Info', family: 'inspect', media: EDITOR_PANEL_MEDIA.document },
  { id: 'histogram', label: 'Analyze', family: 'inspect', media: EDITOR_PANEL_MEDIA.histogram },
  { id: 'history', label: 'History', family: 'flow', media: EDITOR_PANEL_MEDIA.history },
  { id: 'actions', label: 'Actions', family: 'flow', media: EDITOR_PANEL_MEDIA.actions },
  { id: 'notes', label: 'Notes', family: 'flow', media: EDITOR_PANEL_MEDIA.notes },
  { id: 'channels', label: 'Channels', family: 'flow', media: EDITOR_PANEL_MEDIA.channels },
  { id: 'export', label: 'Export', family: 'output', media: EDITOR_PANEL_MEDIA.export },
  { id: 'agent', label: 'Agent', family: 'output', media: EDITOR_PANEL_MEDIA.agent },
];

const DOCK_MIN_WIDTH = 280;
const DOCK_MAX_WIDTH = 460;

const defaultToolShortcuts: ToolShortcutMap = { move: 'V', marquee: 'M', 'quick-selection': 'W', note: 'N', shape: 'U', pen: 'P', shaper: 'R', knife: 'I', brush: 'B', 'history-brush': 'Y', 'clone-stamp': 'S', 'healing-brush': 'J', dodge: 'O', burn: 'K', eraser: 'E', gradient: 'G', hand: 'H', zoom: 'Z' };

interface RasterHistorySnapshot {
  id: string;
  name: string;
  layerId: string;
  layerName: string;
  raster: RasterTileStore;
}

function storedToolShortcuts(): ToolShortcutMap {
  try {
    const value = JSON.parse(window.localStorage.getItem('maipo-tool-shortcuts') ?? 'null') as Partial<ToolShortcutMap> | null;
    if (value && (Object.keys(defaultToolShortcuts) as ToolId[]).every((id) => typeof value[id] === 'string' && value[id]?.length === 1)) {
      return { ...defaultToolShortcuts, ...value };
    }
  } catch {
    // Invalid shortcut preferences fall back to the familiar defaults.
  }
  return defaultToolShortcuts;
}

const defaultBrush: import('../core/commands').BrushSettings = {
  size: 42,
  hardness: 0.72,
  opacity: 1,
  flow: 0.82,
  spacing: 0.12,
  angle: 0,
  roundness: 1,
  scatter: 0,
  count: 1,
  texture: 0,
  pressureSize: false,
  pressureOpacity: true,
  color: [31, 112, 74, 255],
  mode: 'paint',
};

const defaultPalette = ['#17221b', '#1f704a', '#56a879', '#d4a14d', '#e76f51', '#f5efe4', '#ffffff', '#2b2d31'];

function storedColors(key: string, fallback: string[]): string[] {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) ?? 'null') as unknown;
    if (Array.isArray(value)) {
      const colors = value.filter((item): item is string => typeof item === 'string' && /^#[\da-f]{6}$/i.test(item));
      if (colors.length) return colors.slice(0, 24);
    }
  } catch {
    // Invalid preferences are ignored and replaced on the next change.
  }
  return fallback;
}

function session(document: EditorDocument): EditorSession {
  return new DocumentSession(document, { initiallySaved: document.source?.kind === 'maipo' });
}

function initialDocument(): EditorDocument {
  return createDocument({ name: 'Untitled 1', width: 1280, height: 800, background: 'white' });
}

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable);
}

interface NewDocumentDialogProps {
  onClose: () => void;
  onCreate: (document: EditorDocument) => void;
}

function NewDocumentDialog({ onClose, onCreate }: NewDocumentDialogProps) {
  const [width, setWidth] = useState(1920);
  const [height, setHeight] = useState(1080);
  const [name, setName] = useState('Untitled');
  const [background, setBackground] = useState<'transparent' | 'white' | 'black' | 'solid'>('transparent');
  const [solidColor, setSolidColor] = useState('#111512');
  const [error, setError] = useState('');
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form
        className="dialog new-document-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-document-title"
        onSubmit={(event) => {
          event.preventDefault();
          try {
            onCreate(createDocument({ name: name.trim() || 'Untitled', width, height, background: background === 'solid' ? { type: 'solid', color: solidColor } : background }));
          } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not create document.');
          }
        }}
      >
        <div className="dialog-heading">
          <div><span className="eyebrow">New canvas</span><h2 id="new-document-title">Set up a document</h2></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        <div className="preset-row">
          {[
            ['HD', 1920, 1080],
            ['Square', 2048, 2048],
            ['Portrait', 2400, 3000],
            ['4K', 3840, 2160],
          ].map(([label, presetWidth, presetHeight]) => (
            <button key={String(label)} type="button" onClick={() => { setWidth(Number(presetWidth)); setHeight(Number(presetHeight)); }}>
              <strong>{label}</strong><span>{presetWidth} × {presetHeight}</span>
            </button>
          ))}
        </div>
        <label className="field">Name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} /></label>
        <div className="field-pair">
          <label className="field">Width <span><input type="number" min="1" max="32768" value={width} onChange={(event) => setWidth(Number(event.target.value))} /><em>px</em></span></label>
          <label className="field">Height <span><input type="number" min="1" max="32768" value={height} onChange={(event) => setHeight(Number(event.target.value))} /><em>px</em></span></label>
        </div>
        <fieldset className="background-choice"><legend>Background</legend>
          {(['transparent', 'white', 'black'] as const).map((value) => (
            <label key={value}><input type="radio" name="background" value={value} checked={background === value} onChange={() => setBackground(value)} /><span className={`background-swatch bg-${value}`} /> {value}</label>
          ))}
          <label title="A recolorable, resolution-independent vector fill — change the whole background with one color, exports stay crisp at any size">
            <input type="radio" name="background" value="solid" checked={background === 'solid'} onChange={() => setBackground('solid')} />
            <span className="background-swatch" style={{ background: solidColor }} /> solid color
            {background === 'solid' ? <input type="color" aria-label="Background color" value={solidColor} onChange={(event) => setSolidColor(event.target.value)} style={{ width: 26, height: 20, marginLeft: 4, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} /> : null}
          </label>
        </fieldset>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="dialog-actions"><button type="button" onClick={onClose}>Cancel</button><button type="submit" className="primary">Create document</button></div>
      </form>
    </div>
  );
}

function ReportDialog({ report, onClose }: { report: ImportReport; onClose: () => void }) {
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    const previous = globalThis.document.activeElement;
    const heading = dialog?.querySelector<HTMLElement>('#report-title');
    heading?.focus();
    const containFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog?.contains(event.target)) heading?.focus();
    };
    globalThis.document.addEventListener('focusin', containFocus);
    return () => {
      globalThis.document.removeEventListener('focusin', containFocus);
      if (previous instanceof HTMLElement && previous !== globalThis.document.body && previous.isConnected && !previous.matches(':disabled') && previous.getClientRects().length) previous.focus();
      else globalThis.document.querySelector<HTMLElement>('.panel-tabs [aria-selected="true"]')?.focus();
    };
  }, []);
  const warnings = report.notices.filter((notice) => notice.severity !== 'info').length;
  return (
    <div className="modal-backdrop" role="presentation">
      <section ref={dialogRef} className="dialog report-dialog" role="dialog" aria-modal="true" aria-labelledby="report-title" onKeyDown={(event) => {
        // Modal keystrokes must not trigger editor commands behind the report.
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
        if (event.key !== 'Tab') return;
        const controls = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
        if (!controls.length) return;
        event.preventDefault();
        const current = controls.indexOf(globalThis.document.activeElement as HTMLButtonElement);
        const next = event.shiftKey ? (current <= 0 ? controls.length - 1 : current - 1) : (current + 1) % controls.length;
        controls[next]?.focus();
      }}>
        <div className="dialog-heading">
          <div><span className="eyebrow">Import transparency</span><h2 id="report-title" tabIndex={-1}>{report.codec.toUpperCase()} import report</h2></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        <div className="report-summary"><CircleDot size={18} /><div><strong>{report.sourceName}</strong><span>{warnings ? `${warnings} compatibility notice${warnings === 1 ? '' : 's'}` : 'All detected properties were preserved'}</span></div><code>{report.codec}</code></div>
        <div className="report-list">
          {report.notices.length === 0 ? <div className="empty-state compact"><Check size={20} /><p>No unsupported properties were detected.</p></div> : report.notices.map((notice, index) => (
            <div className={`report-item severity-${notice.severity}`} key={`${notice.feature}:${index}`}>
              <span className="report-dot" /><div><strong>{notice.layerName ?? notice.feature}</strong><p>{notice.message}</p></div><span className="handling">{notice.handling}</span>
            </div>
          ))}
        </div>
        <p className="report-footnote">The original file was not modified. Unknown features are never silently claimed as editable.</p>
        <div className="dialog-actions"><button type="button" className="primary" onClick={onClose}>Continue editing</button></div>
      </section>
    </div>
  );
}

type RecoveryEntry = RecoverySummary | TextDraftRecoverySummary;
const isTextDraftRecovery = (item: RecoveryEntry): item is TextDraftRecoverySummary => 'kind' in item && item.kind === 'text-draft';
async function listAllRecoveries(): Promise<RecoveryEntry[]> {
  const [documents, drafts] = await Promise.allSettled([listRecoveries(), listTextDraftRecoveries()]);
  // A failure in one store must not hide recoverable work in the other.
  if (documents.status === 'rejected' && drafts.status === 'rejected') throw new Error('Local recovery storage is unavailable.');
  return [...(documents.status === 'fulfilled' ? documents.value : []), ...(drafts.status === 'fulfilled' ? drafts.value : [])].sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

function RecoveryDialog({ recoveries, onRestore, onDelete, onClose }: {
  recoveries: RecoveryEntry[];
  onRestore: (item: RecoveryEntry) => void;
  onDelete: (item: RecoveryEntry) => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="dialog recovery-dialog" role="dialog" aria-modal="true" aria-labelledby="recovery-title">
        <div className="dialog-heading"><div><span className="eyebrow">Local recovery</span><h2 id="recovery-title">Recover recent work</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button></div>
        <p className="dialog-copy">Snapshots live only in this browser profile. Opening one creates a separate document tab.</p>
        <div className="recovery-list">
          {recoveries.map((item) => <div className="recovery-item" data-recovery-kind={isTextDraftRecovery(item) ? 'text-draft' : 'document'} key={item.key}><HardDrive size={18} /><div><strong>{item.documentName}{item.generation === 'previous' ? ' · fallback' : ''}</strong><span>{isTextDraftRecovery(item) ? `Unapplied text · ${item.layerName}` : `Revision ${item.revision}`} · {new Date(item.savedAt).toLocaleString()}</span></div><button type="button" onClick={() => onRestore(item)}>Restore</button><button type="button" className="quiet-danger" onClick={() => onDelete(item)}>Delete</button></div>)}
        </div>
      </section>
    </div>
  );
}

interface PaletteAction {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
  disabled?: boolean;
}

interface Action extends PaletteAction {
  id: EditorActionId;
}

function isEditorPanelId(value: string): value is EditorPanelId {
  return Object.hasOwn(EDITOR_PANEL_MEDIA, value);
}

function CommandPalette({ actions, onClose }: { actions: PaletteAction[]; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const shown = actions.filter((action) => action.label.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="palette-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <label><Search size={17} /><input autoFocus placeholder="Search actions…" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        <div className="command-results">
          {shown.map((action) => <button key={action.id} type="button" disabled={action.disabled} onClick={() => { action.run(); onClose(); }}><span>{action.label}</span>{action.hint ? <kbd>{action.hint}</kbd> : null}</button>)}
          {shown.length === 0 ? <p>No matching action</p> : null}
        </div>
      </section>
    </div>
  );
}

const BRUSH_PREVIEW_SIZE = 34;

/**
 * Draws the current tip through the same coverage function the stroke uses, so
 * hardness, roundness, angle and a sampled tip all read truthfully here.
 */
function BrushTipPreview({ brush }: { brush: import('../core/commands').BrushSettings }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = BRUSH_PREVIEW_SIZE;
    canvas.height = BRUSH_PREVIEW_SIZE;
    const image = new ImageData(BRUSH_PREVIEW_SIZE, BRUSH_PREVIEW_SIZE);
    const radiusX = BRUSH_PREVIEW_SIZE / 2 - 1;
    const radiusY = Math.max(0.5, radiusX * Math.max(0.05, Math.min(1, brush.roundness ?? 1)));
    const angle = (brush.angle ?? 0) * Math.PI / 180;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const centre = BRUSH_PREVIEW_SIZE / 2;
    for (let y = 0; y < BRUSH_PREVIEW_SIZE; y += 1) for (let x = 0; x < BRUSH_PREVIEW_SIZE; x += 1) {
      const dx = x + 0.5 - centre;
      const dy = y + 0.5 - centre;
      const coverage = brushTipCoverage(brush.tip, cosine * dx + sine * dy, -sine * dx + cosine * dy, radiusX, radiusY, Math.max(0, Math.min(1, brush.hardness)));
      const offset = (y * BRUSH_PREVIEW_SIZE + x) * 4;
      image.data[offset] = brush.color[0];
      image.data[offset + 1] = brush.color[1];
      image.data[offset + 2] = brush.color[2];
      image.data[offset + 3] = Math.round(coverage * 255 * brush.opacity);
    }
    canvas.getContext('2d')?.putImageData(image, 0, 0);
  }, [brush]);
  return <canvas ref={canvasRef} className="brush-tip-preview" aria-label={`Brush tip preview: ${brush.tip ? 'sampled tip' : 'round tip'}`} />;
}

function OptionsBar({
  tool,
  brush,
  setBrush,
  quickSelectionTolerance,
  setQuickSelectionTolerance,
  exposure,
  setExposure,
  exposureRange,
  setExposureRange,
  protectTones,
  setProtectTones,
  selectionMode,
  setSelectionMode,
  shapeKind,
  setShapeKind,
  gridSettings,
  setGridSettings,
  scaleStrokesEffects,
  setScaleStrokesEffects,
  vectorDrawMode,
  setVectorDrawMode,
  document,
  history,
  vectorObjectSelection,
  editingTarget,
  toolBlockedReason,
  perspectiveClone,
  setPerspectiveClone,
  onManageToolPresets,
}: {
  tool: ToolId;
  brush: import('../core/commands').BrushSettings;
  setBrush: React.Dispatch<React.SetStateAction<import('../core/commands').BrushSettings>>;
  quickSelectionTolerance: number;
  setQuickSelectionTolerance: (value: number) => void;
  exposure: number;
  setExposure: (value: number) => void;
  exposureRange: ExposureRange;
  setExposureRange: (value: ExposureRange) => void;
  protectTones: boolean;
  setProtectTones: (value: boolean) => void;
  selectionMode: SelectionCombineMode;
  setSelectionMode: (mode: SelectionCombineMode) => void;
  shapeKind: ShapeToolKind;
  setShapeKind: (kind: ShapeToolKind) => void;
  gridSettings: GridToolSettings;
  setGridSettings: React.Dispatch<React.SetStateAction<GridToolSettings>>;
  scaleStrokesEffects: boolean;
  setScaleStrokesEffects: (value: boolean) => void;
  vectorDrawMode: VectorDrawMode;
  setVectorDrawMode: (mode: VectorDrawMode) => void;
  document: EditorDocument;
  history: HistoryManager;
  vectorObjectSelection: VectorObjectSelection | null;
  editingTarget: EditingContext;
  toolBlockedReason: string | null;
  perspectiveClone: boolean;
  setPerspectiveClone: (value: boolean) => void;
  onManageToolPresets: () => void;
}) {
  const [constrainProportions, setConstrainProportions] = useState(true);
  const active = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
  const selectedObjectId = active && vectorObjectSelection?.layerId === active.id ? vectorObjectSelection.objectIds[0] : undefined;
  const selectedObject = active && selectedObjectId ? getExtensionObject(active, selectedObjectId) : null;
  const bounds = selectedObject?.bounds ?? (active ? layerBounds(active) : null);
  const activeTransform = selectedObject?.transform ?? active?.transform;
  const parentTransform = selectedObject && active ? layerDocumentTransform(document.root, active.id) ?? active.transform : active ? layerParentTransform(document.root, active.id) ?? IDENTITY_MATRIX : IDENTITY_MATRIX;
  const effectiveTransform = activeTransform ? multiplyMatrix(parentTransform, activeTransform) : IDENTITY_MATRIX;
  const transformDisabled = !active || !bounds || active.locked || active.positionLocked;
  const transformedWidth = active && bounds ? Math.hypot(effectiveTransform[0] * bounds.width, effectiveTransform[1] * bounds.width) : 0;
  const transformedHeight = active && bounds ? Math.hypot(effectiveTransform[2] * bounds.height, effectiveTransform[3] * bounds.height) : 0;
  const applyTransform = (matrix: Matrix, scaleOperation = false) => {
    if (!active || active.locked || active.positionLocked) return;
    const command = selectedObject
      ? scaleOperation && !scaleStrokesEffects
        ? createExtensionScaleCommand(active, selectedObject.id) ?? createExtensionTransformCommand(active, selectedObject.id)
        : createExtensionTransformCommand(active, selectedObject.id)
      : new TransformLayerCommand(active.id);
    if (!command) return;
    history.begin(document, command);
    history.updateLive(document, (live) => isMatrixTransformCommand(live) && live.update(document, matrix));
    history.commitLive(document);
  };
  const transformAroundCenter = (operation: Matrix) => {
    if (!active || !activeTransform || !bounds) return;
    const center = transformPoint(effectiveTransform, { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 });
    const rotated = multiplyMatrix(matrixAround(center, operation), effectiveTransform);
    const inverseParent = invertMatrix(parentTransform);
    if (inverseParent) applyTransform(multiplyMatrix(inverseParent, rotated));
  };
  const resizeDimension = (axis: 'width' | 'height', requested: number) => {
    if (!active || !activeTransform || !bounds || !Number.isFinite(requested) || requested < 1) return;
    const current = axis === 'width' ? transformedWidth : transformedHeight;
    if (current <= 0) return;
    const scale = requested / current;
    const scaleX = axis === 'width' || constrainProportions ? scale : 1;
    const scaleY = axis === 'height' || constrainProportions ? scale : 1;
    const center = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
    applyTransform(multiplyMatrix(activeTransform, matrixAround(center, [scaleX, 0, 0, scaleY, 0, 0])), true);
  };
  const toolLabel = tools.find((candidate) => candidate.id === tool)?.label ?? tool;
  const presets = brushPresets(document);
  const hasPerspectivePlane = perspectivePlanes(document).length > 0;
  const savedToolPresets = toolPresets(document);
  const currentToolPresets = savedToolPresets.filter((preset) => preset.tool === tool);
  const applyToolPreset = (preset: ToolPreset) => {
    if (preset.settings.brush) setBrush(structuredClone(preset.settings.brush));
    if (preset.settings.selectionMode) setSelectionMode(preset.settings.selectionMode);
    if (preset.settings.shapeKind) setShapeKind(preset.settings.shapeKind);
    if (preset.settings.gridSettings) setGridSettings(structuredClone(preset.settings.gridSettings));
    if (preset.settings.quickSelectionTolerance !== undefined) setQuickSelectionTolerance(preset.settings.quickSelectionTolerance);
    if (preset.settings.perspectiveClone !== undefined) setPerspectiveClone(preset.settings.perspectiveClone);
    if (preset.settings.exposure) {
      setExposure(preset.settings.exposure.amount);
      setExposureRange(preset.settings.exposure.range);
      setProtectTones(preset.settings.exposure.protectTones);
    }
  };
  const saveToolPreset = () => {
    const name = window.prompt('Tool preset name', `${toolLabel} ${currentToolPresets.length + 1}`)?.trim();
    if (!name) return;
    const settings: ToolPresetSettings = {};
    if (tool === 'brush' || tool === 'history-brush' || tool === 'clone-stamp' || tool === 'healing-brush' || tool === 'dodge' || tool === 'burn' || tool === 'eraser' || tool === 'quick-selection') settings.brush = structuredClone(brush);
    if (tool === 'marquee' || tool === 'quick-selection') settings.selectionMode = selectionMode;
    if (tool === 'quick-selection') settings.quickSelectionTolerance = quickSelectionTolerance;
    if (tool === 'shape') {
      settings.shapeKind = shapeKind;
      settings.gridSettings = structuredClone(gridSettings);
    }
    if (tool === 'clone-stamp') settings.perspectiveClone = perspectiveClone;
    if (tool === 'dodge' || tool === 'burn') settings.exposure = { amount: exposure, range: exposureRange, protectTones };
    history.execute(document, new SetToolPresetsCommand([...savedToolPresets, createToolPreset(name, tool, settings)], 'Save tool preset'));
  };
  const saveBrushPreset = () => {
    const name = window.prompt('Brush preset name', `Brush ${presets.length + 1}`)?.trim();
    if (!name) return;
    history.execute(document, new SetBrushPresetsCommand([...presets, createBrushPreset(name, brush)]));
  };
  const defineBrushFromSelection = () => {
    if (!active || active.kind !== 'raster') return;
    const tip = sampleBrushTip(document, active.id);
    if (!tip) return;
    const name = window.prompt('Sampled brush preset name', `Sampled brush ${presets.length + 1}`)?.trim();
    if (!name) return;
    history.execute(document, new SetBrushPresetsCommand([...presets, createBrushPreset(name, { ...brush, tip })]));
    setBrush((value) => ({ ...value, tip }));
  };
  return (
    <div className="tool-settings" role="group" aria-label={`${toolLabel} settings`}>
      <div
        className={`editing-context-badge context-${editingTarget.family}`}
        aria-label={`Editing target: ${editingTarget.label}, ${editingTarget.detail}`}
        title="The active editing target; other layer types in the document keep their own semantics."
      ><i /><span>{editingTarget.label}</span><small>{editingTarget.detail}</small></div>
      {toolBlockedReason ? <span className="context-blocked-reason" role="status">{toolBlockedReason}</span> : null}
      <label>Tool preset <select defaultValue="" onChange={(event) => {
        const preset = currentToolPresets.find((item) => item.id === event.target.value);
        if (preset) applyToolPreset(preset);
        event.currentTarget.value = '';
      }}><option value="">Choose…</option>{currentToolPresets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}</select></label>
      <button type="button" disabled={savedToolPresets.length >= 200} onClick={saveToolPreset}>Save tool preset</button>
      <button type="button" onClick={onManageToolPresets}>Manage…</button>
      {(tool === 'brush' || tool === 'history-brush' || tool === 'clone-stamp' || tool === 'healing-brush' || tool === 'eraser') ? <SettingsSection title="Essentials">
        <label>Size <input type="range" min="1" max="400" value={brush.size} onChange={(event) => setBrush((value) => ({ ...value, size: Number(event.target.value) }))} /><input className="mini-number" aria-label={`${toolLabel} size in pixels`} type="number" min="1" max="1000" value={brush.size} onChange={(event) => setBrush((value) => ({ ...value, size: Number(event.target.value) }))} /> px</label>
        <label>Hardness <input type="range" min="0" max="1" step="0.01" value={brush.hardness} onChange={(event) => setBrush((value) => ({ ...value, hardness: Number(event.target.value) }))} /><output>{Math.round(brush.hardness * 100)}%</output></label>
        <label>Opacity <input type="range" min="0.01" max="1" step="0.01" value={brush.opacity} onChange={(event) => setBrush((value) => ({ ...value, opacity: Number(event.target.value) }))} /><output>{Math.round(brush.opacity * 100)}%</output></label>
        <label>Flow <input type="range" min="0.01" max="1" step="0.01" value={brush.flow} onChange={(event) => setBrush((value) => ({ ...value, flow: Number(event.target.value) }))} /><output>{Math.round(brush.flow * 100)}%</output></label>
      </SettingsSection> : null}
      {tool === 'quick-selection' ? <SettingsSection title="Essentials">
        <label>Brush diameter <input type="range" min="3" max="400" value={brush.size} onChange={(event) => setBrush((value) => ({ ...value, size: Number(event.target.value) }))} /><input className="mini-number" aria-label={"Brush diameter in pixels"} type="number" min="3" max="1024" value={brush.size} onChange={(event) => setBrush((value) => ({ ...value, size: Number(event.target.value) }))} /> px</label>
        <label>Tolerance <input type="range" min="1" max="255" value={quickSelectionTolerance} onChange={(event) => setQuickSelectionTolerance(Number(event.target.value))} /><output>{quickSelectionTolerance}</output></label>
        <span className="option-hint">Paint to grow through locally connected color and contrast; work is capped per sample.</span>
      </SettingsSection> : null}
      {tool === 'dodge' || tool === 'burn' ? <SettingsSection title="Exposure">
        <label>Size <input type="range" min="1" max="400" value={brush.size} onChange={(event) => setBrush((value) => ({ ...value, size: Number(event.target.value) }))} /><input className="mini-number" aria-label={`${toolLabel} size in pixels`} type="number" min="1" max="512" value={brush.size} onChange={(event) => setBrush((value) => ({ ...value, size: Number(event.target.value) }))} /> px</label>
        <label>Exposure <input type="range" min="1" max="100" value={Math.round(exposure * 100)} onChange={(event) => setExposure(Number(event.target.value) / 100)} /><output>{Math.round(exposure * 100)}%</output></label>
        <label>Range <select value={exposureRange} onChange={(event) => setExposureRange(event.target.value as ExposureRange)}><option value="shadows">Shadows</option><option value="midtones">Midtones</option><option value="highlights">Highlights</option></select></label>
        <label><input type="checkbox" checked={protectTones} onChange={(event) => setProtectTones(event.target.checked)} /> Protect tones</label>
      </SettingsSection> : null}
      {tool === 'brush' ? <SettingsSection title="Tip and dynamics">
        <label>Spacing <input className="mini-number" type="number" min="0.01" max="5" step="0.01" value={brush.spacing} onChange={(event) => setBrush((value) => ({ ...value, spacing: Number(event.target.value) }))} /></label>
        <label>Angle <input className="mini-number" type="number" min="-180" max="180" value={brush.angle ?? 0} onChange={(event) => setBrush((value) => ({ ...value, angle: Number(event.target.value) }))} />°</label>
        <label>Roundness <input type="range" min="0.05" max="1" step="0.01" value={brush.roundness ?? 1} onChange={(event) => setBrush((value) => ({ ...value, roundness: Number(event.target.value) }))} /><output>{Math.round((brush.roundness ?? 1) * 100)}%</output></label>
        <label>Scatter <input type="range" min="0" max="2" step="0.01" value={brush.scatter ?? 0} onChange={(event) => setBrush((value) => ({ ...value, scatter: Number(event.target.value) }))} /><output>{Math.round((brush.scatter ?? 0) * 100)}%</output></label>
        <label>Count <input className="mini-number" type="number" min="1" max="8" value={brush.count ?? 1} onChange={(event) => setBrush((value) => ({ ...value, count: Number(event.target.value) }))} /></label>
        <label>Texture <input type="range" min="0" max="1" step="0.01" value={brush.texture ?? 0} onChange={(event) => setBrush((value) => ({ ...value, texture: Number(event.target.value) }))} /><output>{Math.round((brush.texture ?? 0) * 100)}%</output></label>
        <label><input type="checkbox" checked={brush.pressureSize ?? false} onChange={(event) => setBrush((value) => ({ ...value, pressureSize: event.target.checked }))} /> Pressure size</label>
        <label><input type="checkbox" checked={brush.pressureOpacity !== false} onChange={(event) => setBrush((value) => ({ ...value, pressureOpacity: event.target.checked }))} /> Pressure opacity</label>
        <label>Size jitter <input type="range" min="0" max="1" step="0.01" value={brush.sizeJitter ?? 0} onChange={(event) => setBrush((value) => ({ ...value, sizeJitter: Number(event.target.value) }))} /><output>{Math.round((brush.sizeJitter ?? 0) * 100)}%</output></label>
        <label>Opacity jitter <input type="range" min="0" max="1" step="0.01" value={brush.opacityJitter ?? 0} onChange={(event) => setBrush((value) => ({ ...value, opacityJitter: Number(event.target.value) }))} /><output>{Math.round((brush.opacityJitter ?? 0) * 100)}%</output></label>
        <BrushTipPreview brush={brush} />
        <label>Preset <select defaultValue="" onChange={(event) => {
          const preset = presets.find((candidate) => candidate.id === event.target.value);
          if (preset) setBrush((value) => {
            const next = { ...value, ...preset.settings };
            if (!preset.settings.tip) delete next.tip;
            return next;
          });
          event.currentTarget.value = '';
        }}><option value="">Choose…</option>{presets.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}</select></label>
        <button type="button" onClick={saveBrushPreset}>Save preset</button>
        <button type="button" disabled={active?.kind !== 'raster' || document.selection.inverted || document.selection.regions.length === 0} onClick={defineBrushFromSelection}>Define from selection</button>
        <button type="button" disabled={!brush.tip} onClick={() => setBrush((value) => {
          const next = { ...value };
          delete next.tip;
          return next;
        })}>Round tip</button>
      </SettingsSection> : null}
      {tool === 'clone-stamp' ? <><label><input type="checkbox" checked={perspectiveClone && hasPerspectivePlane} disabled={!hasPerspectivePlane} onChange={(event) => setPerspectiveClone(event.target.checked)} /> Vanishing Point plane</label><span className="option-hint">Alt/Option-click a source, then paint{perspectiveClone && hasPerspectivePlane ? ' through the saved perspective plane' : ' with an aligned offset'}.</span></> : null}
      {tool === 'healing-brush' ? <><span className="option-hint">Paint to copy nearby texture while matching local color.</span><button type="button" disabled={active?.kind !== 'raster' || document.selection.inverted || document.selection.regions.length === 0} onClick={() => active?.kind === 'raster' && history.execute(document, new HealSelectionCommand(active.id))}>Heal selected patch</button></> : null}
      {tool === 'marquee' || tool === 'quick-selection' ? <div className="segmented-control" aria-label="Selection combine mode">
        {(tool === 'quick-selection' ? ['replace', 'add', 'subtract'] as const : ['replace', 'add', 'subtract', 'intersect'] as const).map((mode) => <button type="button" key={mode} className={selectionMode === mode ? 'active' : ''} onClick={() => setSelectionMode(mode)}>{mode}</button>)}
      </div> : null}
      {tool === 'shape' ? <SettingsSection title="Shape">
        <div className="segmented-control" aria-label="Shape kind">
          {(['rectangle', 'ellipse', 'rectangular-grid', 'polar-grid'] as const).map((kind) => <button type="button" key={kind} className={shapeKind === kind ? 'active' : ''} onClick={() => setShapeKind(kind)}>{kind.replace('-', ' ')}</button>)}
        </div>
        {shapeKind === 'rectangular-grid' ? <><label>Horizontal dividers <input className="mini-number" type="number" min="0" max="64" value={gridSettings.horizontalDividers} onChange={(event) => setGridSettings((value) => ({ ...value, horizontalDividers: Number(event.target.value) }))} /></label><label>Vertical dividers <input className="mini-number" type="number" min="0" max="64" value={gridSettings.verticalDividers} onChange={(event) => setGridSettings((value) => ({ ...value, verticalDividers: Number(event.target.value) }))} /></label></> : null}
        {shapeKind === 'polar-grid' ? <><label>Concentric dividers <input className="mini-number" type="number" min="0" max="64" value={gridSettings.concentricDividers} onChange={(event) => setGridSettings((value) => ({ ...value, concentricDividers: Number(event.target.value) }))} /></label><label>Radial dividers <input className="mini-number" type="number" min="2" max="64" value={gridSettings.radialDividers} onChange={(event) => setGridSettings((value) => ({ ...value, radialDividers: Number(event.target.value) }))} /></label></> : null}
        <div className="segmented-control" aria-label="Vector draw mode">
          {(['normal', 'behind', 'inside'] as const).map((mode) => <button type="button" key={mode} className={vectorDrawMode === mode ? 'active' : ''} aria-pressed={vectorDrawMode === mode} disabled={mode === 'inside' && (active?.kind !== 'vector' || active.clipping || active.vector.objects.length !== 1)} title={mode === 'inside' ? 'Clip new vectors inside the active one-object vector layer' : undefined} onClick={() => setVectorDrawMode(mode)}>{mode === 'normal' ? 'Draw Normal' : mode === 'behind' ? 'Draw Behind' : 'Draw Inside'}</button>)}
        </div>
        <span className="option-hint">Drag to draw · Shift makes it equal-sided · Alt draws from center</span>
      </SettingsSection> : null}
      {tool === 'pen' ? <span className="option-hint">Click for corner nodes · drag for smooth handles · Enter commits · Escape cancels</span> : null}
      {tool === 'shaper' ? <span className="option-hint">Draw a closed rough circle, rectangle, or triangle · recognized gestures become editable vectors</span> : null}
      {tool === 'knife' ? <span className="option-hint">Drag a cut line across selected filled vector artwork · one gesture, one undo</span> : null}
      {tool === 'note' ? <span className="option-hint">Click inside the document to pin a local note, then edit it in the Notes panel.</span> : null}
      {tool === 'move' ? <SettingsSection title="Transform">
        <span className="options-hint">⌘-drag to box-select objects</span>
        <label>X <input aria-label="Layer X position" className="coordinate-input" type="number" value={Math.round(activeTransform?.[4] ?? 0)} disabled={transformDisabled} onChange={(event) => activeTransform && applyTransform([activeTransform[0], activeTransform[1], activeTransform[2], activeTransform[3], Number(event.target.value), activeTransform[5]])} /></label>
        <label>Y <input aria-label="Layer Y position" className="coordinate-input" type="number" value={Math.round(activeTransform?.[5] ?? 0)} disabled={transformDisabled} onChange={(event) => activeTransform && applyTransform([activeTransform[0], activeTransform[1], activeTransform[2], activeTransform[3], activeTransform[4], Number(event.target.value)])} /></label>
        <label>W <input aria-label="Layer width" className="coordinate-input" type="number" min="1" value={Math.round(transformedWidth)} disabled={transformDisabled} onChange={(event) => resizeDimension('width', Number(event.target.value))} /></label>
        <button
          type="button"
          className={constrainProportions ? 'active' : ''}
          aria-label={constrainProportions ? 'Unlink width and height' : 'Link width and height'}
          title={constrainProportions ? 'Width and height are linked' : 'Width and height are independent'}
          disabled={transformDisabled}
          onClick={() => setConstrainProportions((value) => !value)}
        >{constrainProportions ? <Link2 size={13} /> : <Unlink2 size={13} />}</button>
        <label>H <input aria-label="Layer height" className="coordinate-input" type="number" min="1" value={Math.round(transformedHeight)} disabled={transformDisabled} onChange={(event) => resizeDimension('height', Number(event.target.value))} /></label>
        <span className="options-divider" />
        <button type="button" disabled={transformDisabled} onClick={() => transformAroundCenter([0, 1, -1, 0, 0, 0])}><RotateCw size={14} /> Rotate 90°</button>
        <button type="button" disabled={transformDisabled} onClick={() => transformAroundCenter([-1, 0, 0, 1, 0, 0])}>Flip horizontal</button>
        <button type="button" disabled={transformDisabled} onClick={() => transformAroundCenter([1, 0, 0, -1, 0, 0])}>Flip vertical</button>
        <button type="button" disabled={transformDisabled} onClick={() => applyTransform([1, 0, 0, 1, 0, 0], true)}>Reset transform</button>
        <label><input aria-label="Scale strokes and effects" type="checkbox" checked={scaleStrokesEffects} onChange={(event) => setScaleStrokesEffects(event.target.checked)} /> Scale strokes &amp; effects</label>
        <span className="option-hint">Drag handles to resize or rotate · corners keep proportions · Shift unlocks</span>
      </SettingsSection> : null}
      {tool === 'hand' ? <span className="option-hint">Drag to pan · Space temporarily activates Hand</span> : null}
      {tool === 'gradient' ? <span className="option-hint">Drag across a raster layer · foreground fades to background · selection is respected</span> : null}
      {tool === 'zoom' ? <span className="option-hint">Click to zoom in · Alt-click to zoom out</span> : null}
    </div>
  );
}

/** Track whether the browser is offering to install the app (and hide once installed). */
function useInstallable(): boolean {
  const [installable, setInstallable] = useState(() => isInstallAvailable() && !isStandalone());
  useEffect(() => subscribeInstallable(() => setInstallable(isInstallAvailable() && !isStandalone())), []);
  return installable;
}

export function Workspace() {
  const [sessions, setSessions] = useState<EditorSession[]>(() => [session(initialDocument())]);
  const sourceEdits = useRef(new Map<string, ImageSourceEdit>());
  const sessionsRef = useRef(sessions);
  const reloadRecoveryInFlight = useRef(false);
  useLayoutEffect(() => { sessionsRef.current = sessions; }, [sessions]);
  const [activeId, setActiveId] = useState(() => sessions[0]?.sessionId ?? '');
  const installable = useInstallable();
  const [revision, setRevision] = useState(0);
  const [tool, setTool] = useState<ToolId>('brush');
  const [brush, setBrush] = useState(defaultBrush);
  const [quickSelectionTolerance, setQuickSelectionTolerance] = useState(48);
  const [exposure, setExposure] = useState(0.2);
  const [exposureRange, setExposureRange] = useState<ExposureRange>('midtones');
  const [protectTones, setProtectTones] = useState(true);
  const [perspectiveClone, setPerspectiveClone] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#f5efe4');
  const [recentColors, setRecentColors] = useState<string[]>(() => storedColors('maipo-recent-colors', ['#1f704a']));
  const [paletteColors, setPaletteColors] = useState<string[]>(() => storedColors('maipo-palette', defaultPalette));
  const [selectionMode, setSelectionMode] = useState<SelectionCombineMode>('replace');
  const [shapeKind, setShapeKind] = useState<ShapeToolKind>('rectangle');
  const [gridSettings, setGridSettings] = useState<GridToolSettings>({ horizontalDividers: 5, verticalDividers: 5, concentricDividers: 4, radialDividers: 8 });
  const [scaleStrokesEffects, setScaleStrokesEffects] = useState(() => window.localStorage.getItem('maipo-scale-strokes-effects') !== 'false');
  const [vectorDrawMode, setVectorDrawMode] = useState<VectorDrawMode>('normal');
  const [rightTab, setRightTab] = useState<'layers' | 'fill' | 'history' | 'actions' | 'channels' | 'export' | 'notes' | 'document' | 'histogram' | 'agent' | `addon:${string}`>('layers');
  const [addonRevision, setAddonRevision] = useState(0);
  useEffect(() => {
    const unsubscribe = subscribeToAddons(() => setAddonRevision((value) => value + 1));
    // The async loader can register an add-on between the initial render and
    // this passive effect. Reconcile once after subscribing so that event is
    // never lost; later registrations arrive through the listener above.
    setAddonRevision((value) => value + 1);
    return unsubscribe;
  }, []);
  const addonPanels = useMemo(
    () => listAddons().flatMap((addon) => (addon.panels ?? []).map((panel) => ({ addon, panel }))),
    // recomputed when an addon
    // registers; listAddons() reads a module-level registry and is otherwise stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addonRevision],
  );
  const addonCommands = useMemo(
    () => listAddons().flatMap((addon) => (addon.commands ?? []).map((command) => ({ addon, command }))),
    // See addonPanels: both projections refresh from the same registry event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addonRevision],
  );
  const [colorOpen, setColorOpen] = useState(() => window.localStorage.getItem('maipo-color-open') !== 'false');
  const [newDialog, setNewDialog] = useState(false);
  const [portableCanvasEdit, setPortableCanvasEdit] = useState<{ session: DocumentSession; layer: TextLayer; point: Point | null; recovered?: { key: string; value: string } } | null>(null);
  const [portableTextSession, setPortableTextSession] = useState<DocumentSession | null>(null);
  const [geometryDialog, setGeometryDialog] = useState<GeometryDialogMode | null>(null);
  const [deformDialog, setDeformDialog] = useState<DeformDialogMode | null>(null);
  const [applyImageDialog, setApplyImageDialog] = useState(false);
  const [colorRangeDialog, setColorRangeDialog] = useState(false);
  const [colourTraceDialog, setColourTraceDialog] = useState(false);
  // Which group the pointer is working INSIDE. Deliberately not IsolationScope:
  // isolation hides and dims everything else, and you group parts of a figure
  // precisely so you can edit them in context.
  const [groupEntry, setGroupEntry] = useState<{ layerId: string; groupId: string } | null>(null);
  const [vanishingPointDialog, setVanishingPointDialog] = useState(false);
  const [batchProcessDialog, setBatchProcessDialog] = useState(false);
  const [dataDrivenDialog, setDataDrivenDialog] = useState(false);
  const [highPassDialog, setHighPassDialog] = useState(false);
  const [displaceDialog, setDisplaceDialog] = useState(false);
  const [matchColorDialog, setMatchColorDialog] = useState(false);
  const [luminosityMasksDialog, setLuminosityMasksDialog] = useState(false);
  const [contentAwareScaleDialog, setContentAwareScaleDialog] = useState(false);
  const [guideDialog, setGuideDialog] = useState(false);
  const [guideLayoutDialog, setGuideLayoutDialog] = useState(false);
  const [shortcutDialog, setShortcutDialog] = useState(false);
  const [toolPresetsDialog, setToolPresetsDialog] = useState(false);
  const [toolShortcuts, setToolShortcuts] = useState<ToolShortcutMap>(storedToolShortcuts);
  const [showGrid, setShowGrid] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const [showRulers, setShowRulers] = useState(true);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [recoveries, setRecoveries] = useState<RecoveryEntry[]>([]);
  const [recoveryDialog, setRecoveryDialog] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [temporaryHand, setTemporaryHand] = useState(false);
  const [maskEditingLayerId, setMaskEditingLayerId] = useState<string | null>(null);
  const [vectorObjectSelection, setVectorObjectSelection] = useState<VectorObjectSelection | null>(null);
  const [isolationScope, setIsolationScope] = useState<IsolationScope | null>(null);
  const [directTextEditingLayerId, setDirectTextEditingLayerId] = useState<string | null>(null);
  const [readout, setReadout] = useState<PointerReadout>({ x: 0, y: 0, inside: false });
  const [viewport, setViewport] = useState<Viewport>({ width: 1, height: 1, zoom: 1, panX: 0, panY: 0, dpr: 1 });
  const [renderStats, setRenderStats] = useState<RenderStats>({ renderedTiles: 0, elapsedMs: 0, backend: 'canvas2d', tileCacheHits: 0, layerCacheHits: 0, cacheHitRate: 1, canvasAllocations: 0 });
  const [progress, setProgress] = useState<{ message: string; value: number; controller: AbortController } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [dragSide, setDragSide] = useState<'layer' | 'open'>('open');
  const [layerClipboard, setLayerClipboard] = useState<LayerClipboard | null>(null);
  const [historySnapshots, setHistorySnapshots] = useState<Record<string, RasterHistorySnapshot[]>>({});
  const [activeHistorySnapshots, setActiveHistorySnapshots] = useState<Record<string, string>>({});
  const [quickMaskSessions, setQuickMaskSessions] = useState<Record<string, boolean>>({});
  const [channelViews, setChannelViews] = useState<Record<string, ChannelView>>({});
  const [refineSelectionSource, setRefineSelectionSource] = useState<SelectionState | null>(null);
  // The media workspace filters the interface only. It is stored per browser and
  // never written into a .maipo file — opening someone else's document must not
  // rearrange your tools. See ADR-004.
  const [mediaMode, setMediaMode] = useState<MediaMode>(() => storedMediaMode());
  const [mediaAnnouncement, setMediaAnnouncement] = useState('');
  const [dockFamily, setDockFamily] = useState<PanelFamily>('build');
  const [dockWidth, setDockWidth] = useState(() => {
    const stored = Number(window.localStorage.getItem('maipo-dock-width'));
    return Number.isFinite(stored) && stored >= DOCK_MIN_WIDTH && stored <= DOCK_MAX_WIDTH ? stored : 328;
  });
  const [panelBrowser, setPanelBrowser] = useState(false);
  const dockDragging = useRef(false);
  const dockWidthRef = useRef(0);

  /** Ends a drag from any of the three ways a pointer interaction can finish. */
  const endDockDrag = (element: Element, pointerId: number) => {
    if (!dockDragging.current) return;
    dockDragging.current = false;
    if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
    if (dockWidthRef.current) window.localStorage.setItem('maipo-dock-width', String(dockWidthRef.current));
  };
  const visibleTools = useMemo(() => tools.filter((item) => visibleIn(item.media, mediaMode)), [mediaMode]);

  /** Built-ins plus add-on contributions, as one list the dock can filter. */
  const allDockPanels = useMemo(() => [
    ...DOCK_PANELS.map((panel) => ({ ...panel, addon: null as string | null, allOnly: false })),
    ...addonPanels.map(({ addon, panel }) => ({
      id: `addon:${addon.id}:${panel.id}`,
      label: panel.label,
      family: 'ext' as PanelFamily,
      // Optional API-v1 media declarations are backwards-compatible. An old
      // add-on that omits one remains available from All, but cannot leak into
      // either focused workspace.
      media: panel.media ?? NEUTRAL,
      addon: `${addon.name} ${addon.version}`,
      allOnly: panel.media === undefined,
    })),
  ], [addonPanels]);

  const dockPanelOffered = useCallback(
    (panel: (typeof allDockPanels)[number], mode: MediaMode = mediaMode) =>
      panel.allOnly ? mode === 'all' : offeredIn(panel.media, mode, false),
    [mediaMode],
  );

  const panelsInFamily = (family: PanelFamily) =>
    allDockPanels.filter((panel) => panel.family === family && dockPanelOffered(panel));

  const dockId = useId();
  const visibleFamilies = PANEL_FAMILIES.filter((family) => panelsInFamily(family.id).length > 0);
  const familyPanels = panelsInFamily(dockFamily);
  const selectDockFamily = (family: PanelFamily) => {
    const first = panelsInFamily(family)[0];
    if (!first) return;
    setDockFamily(family);
    setRightTab(first.id as typeof rightTab);
  };
  const familyRoving = useRoving(visibleFamilies.length, Math.max(0, visibleFamilies.findIndex((family) => family.id === dockFamily)), (index) => {
    const family = visibleFamilies[index];
    if (family) selectDockFamily(family.id);
  });
  const panelRoving = useRoving(familyPanels.length, Math.max(0, familyPanels.findIndex((panel) => panel.id === rightTab)), (index) => {
    const panel = familyPanels[index];
    if (panel) setRightTab(panel.id as typeof rightTab);
  });

  // A workspace can hide the panel that is currently open — Channels and
  // Analyze are raster-only. Fall back to a surviving panel rather than
  // rendering an empty dock.
  //
  // This used to run during render via queueMicrotask, which schedules from an
  // uncommitted render: a restarted concurrent render could dispatch a stale
  // fallback, and StrictMode scheduled it twice. It also corrected only the
  // panel, leaving the family pointing somewhere that panel could never live.
  const openPanelHidden = !allDockPanels.some((panel) => panel.id === rightTab && dockPanelOffered(panel));
  useEffect(() => {
    if (!openPanelHidden) return;
    const survivors = allDockPanels.filter((panel) => dockPanelOffered(panel));
    const fallback = survivors.find((panel) => panel.family === dockFamily) ?? survivors[0];
    if (!fallback || fallback.id === rightTab) return;
    const dockOwnedFocus = globalThis.document.activeElement instanceof HTMLElement
      && Boolean(globalThis.document.activeElement.closest('.right-dock'));
    setDockFamily(fallback.family);
    setRightTab(fallback.id as typeof rightTab);
    setMediaAnnouncement(`${modeLabel(mediaMode)} workspace. The active dock panel is hidden here, so the dock fell back to ${fallback.label}; no document content changed.`);
    if (dockOwnedFocus) window.requestAnimationFrame(() => globalThis.document.querySelector<HTMLElement>('.right-dock .panel-tabs [role="tab"][aria-selected="true"]')?.focus());
  }, [openPanelHidden, allDockPanels, mediaMode, rightTab, dockFamily, dockPanelOffered]);

  const hiddenToolCount = tools.length - visibleTools.length;
  const visibleAddonPanels = useMemo(
    () => addonPanels.filter(({ panel }) => panel.media ? offeredIn(panel.media, mediaMode, false) : mediaMode === 'all'),
    [addonPanels, mediaMode],
  );
  const [theme, setTheme] = useState<'dark' | 'light'>(() => window.localStorage.getItem('maipo-theme') === 'light' ? 'light' : 'dark');
  const [density, setDensity] = useState<'compact' | 'comfortable'>(() => window.localStorage.getItem('maipo-density') === 'comfortable' ? 'comfortable' : 'compact');
  const [viewAction, setViewAction] = useState<{ id: number; action: 'fit' | 'in' | 'out' }>({ id: 0, action: 'fit' });
  const [recordingActionName, setRecordingActionName] = useState<string | null>(null);
  const [recordedStepCount, setRecordedStepCount] = useState(0);
  const recordingActionRef = useRef<{ name: string; steps: ActionStep[] } | null>(null);
  const replayingActionRef = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const placeImageInput = useRef<HTMLInputElement>(null);
  const fontInput = useRef<HTMLInputElement>(null);
  const menuBarRef = useRef<HTMLElement>(null);
  const registry = useMemo(createDefaultCodecRegistry, []);
  const exportRenderer = useMemo(() => new Canvas2DRenderer(), []);
  const active = sessions.find((item) => item.sessionId === activeId) ?? sessions[0];
  const document = active?.document;
  const history = active?.history;
  const previousActive = useRef<EditorSession | undefined>(active);
  const activeSessionRef = useRef<EditorSession | undefined>(active);
  activeSessionRef.current = active;
  // Agent document lifecycle (maipo_new_document / maipo_close_document): the same paths as File → New and a tab's close button.
  const agentSessionActions = useRef<{ add: (next: EditorDocument) => EditorSession; remove: (id: string) => void } | null>(null);

  useEffect(() => {
    // Test-only hook: compiled out unless this is a dev server or a MAIPO_TEST_HOOKS=1 build.
    if (!import.meta.env.DEV && !__MAIPO_TEST_HOOKS__) return;
    let uninstall: (() => void) | undefined;
    let cancelled = false;
    void import('./test-hooks').then(({ installTestHooks }) => {
      if (!cancelled) uninstall = installTestHooks(() => activeSessionRef.current);
    });
    return () => { cancelled = true; uninstall?.(); };
  }, []);

  useEffect(() => {
    // In-page agent API (ADR-009 phase 2): compiled out of production builds
    // (neither a dev server nor MAIPO_AGENT_API=1), and even then installed
    // only when the page was opened with ?agent-dev=1.
    if (!import.meta.env.DEV && !__MAIPO_AGENT_API__) return;
    if (new URLSearchParams(window.location.search).get('agent-dev') !== '1') return;
    let uninstall: (() => void) | undefined;
    let cancelled = false;
    void import('../agent/page-api').then(({ installPageAgentApi }) => {
      if (cancelled) return;
      uninstall = installPageAgentApi({
        sessions: () => sessionsRef.current,
        focused: () => activeSessionRef.current,
        renderer: () => exportRenderer,
      });
    });
    return () => { cancelled = true; uninstall?.(); };
  }, [exportRenderer]);

  useEffect(() => {
    // Agent access (ADR-009 phase 3). Lends the sessions to the gateway, but
    // opens nothing: a loopback connection needs a user act (Allow on a
    // pairing link, Connect in the Agent panel) or a user-granted
    // "Connect automatically" pairing.
    registerAgentHost({
      sessions: () => sessionsRef.current,
      focused: () => activeSessionRef.current,
      renderer: () => exportRenderer,
      checkpoint: async (target, label, slot) => { await saveRecoveryCheckpoint(target, label, slot); },
      addSession: (next) => {
        const actions = agentSessionActions.current;
        if (!actions) throw new Error('The workspace is not ready.');
        return actions.add(next);
      },
      closeSession: (target) => agentSessionActions.current?.remove(target.sessionId),
    });
    // A pairing link's fragment is stripped at once; it only fills the Allow dialog.
    const link = parsePairingFragment(window.location.hash);
    if (link.ok || link.reason !== 'absent') {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
      updateAgentState(link.ok ? { pendingLink: link.link, linkError: null } : { linkError: `This agent pairing link cannot be used: ${link.reason}` });
    } else {
      void startAgentAccess().catch(() => undefined);
    }
    return () => registerAgentHost(null);
  }, [exportRenderer]);

  useEffect(() => {
    const previous = previousActive.current;
    if (previous && previous !== active) {
      previous.history.cancelLive(previous.document);
      setPortableTextSession(null);
      setPortableCanvasEdit(current => current?.session === active ? current : null);
      setMaskEditingLayerId(null);
      setVectorObjectSelection(null);
      setIsolationScope(null);
      setDirectTextEditingLayerId(null);
    }
    previousActive.current = active;
  }, [active]);

  const closeMenus = useCallback((except?: HTMLDetailsElement) => {
    menuBarRef.current?.querySelectorAll<HTMLDetailsElement>('details[open]').forEach((menu) => {
      if (menu !== except) {
        // A command can open a modal whose focus trap remembers this element.
        // Return to the visible heading before hiding its focused menu item.
        if (menu.contains(globalThis.document.activeElement)) menu.querySelector('summary')?.focus();
        menu.open = false;
      }
    });
  }, []);

  useEffect(() => {
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuBarRef.current?.contains(event.target)) closeMenus();
    };
    globalThis.document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => globalThis.document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [closeMenus]);

  useEffect(() => {
    if (!history) return;
    return history.subscribe(() => setRevision((value) => value + 1));
  }, [history]);

  useEffect(() => {
    if (!history) return;
    return history.subscribeCommands((command) => {
      const draft = recordingActionRef.current;
      if (!draft || replayingActionRef.current) return;
      const step = command.toActionStep?.() ?? { type: 'marker' as const, label: command.label, replayable: false as const };
      draft.steps.push(structuredClone(step));
      setRecordedStepCount(draft.steps.length);
    });
  }, [history]);

  useEffect(() => {
    if (!document || !maskEditingLayerId || (document.activeLayerIds.includes(maskEditingLayerId) && findLayer(document.root, maskEditingLayerId)?.mask)) return;
    setMaskEditingLayerId(null);
  }, [document, maskEditingLayerId, revision]);

  useEffect(() => {
    if (!vectorObjectSelection || !document) return;
    const layer = findLayer(document.root, vectorObjectSelection.layerId);
    const objectIds = layer?.kind === 'vector'
      ? vectorObjectSelection.objectIds.filter((id) => layer.vector.objects.some((object) => object.id === id))
      : [];
    if (!document.activeLayerIds.includes(vectorObjectSelection.layerId) || objectIds.length === 0) setVectorObjectSelection(null);
    else if (objectIds.length !== vectorObjectSelection.objectIds.length) setVectorObjectSelection({ ...vectorObjectSelection, objectIds });
  }, [document, revision, vectorObjectSelection]);

  useEffect(() => {
    if (!document || !walkLayers(document.root).some((layer) => layer.kind === 'vector' || layer.kind === 'text')) return;
    let current = true;
    void import('../vector/runtime').then(({ activateVectorSubsystem }) => {
      activateVectorSubsystem();
      if (current) setRevision((value) => value + 1);
    });
    return () => { current = false; };
  }, [document]);

  useEffect(() => {
    window.localStorage.setItem('maipo-theme', theme);
    window.localStorage.setItem('maipo-density', density);
  }, [theme, density]);

  useEffect(() => {
    window.localStorage.setItem('maipo-recent-colors', JSON.stringify(recentColors));
    window.localStorage.setItem('maipo-palette', JSON.stringify(paletteColors));
  }, [recentColors, paletteColors]);

  useEffect(() => {
    window.localStorage.setItem('maipo-tool-shortcuts', JSON.stringify(toolShortcuts));
  }, [toolShortcuts]);

  useEffect(() => {
    void listAllRecoveries().then(setRecoveries).catch(() => setRecoveries([]));
  }, []);

  useEffect(() => {
    void loadStoredFonts().then((restored) => {
      if (restored > 0) setRevision((value) => value + 1);
    });
    const repaintOnFontLoad = () => setRevision((value) => value + 1);
    globalThis.document.fonts.addEventListener('loadingdone', repaintOnFontLoad);
    return () => globalThis.document.fonts.removeEventListener('loadingdone', repaintOnFontLoad);
  }, []);

  const installFonts = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    const result = await installFontFiles(files);
    const names = [...new Set(result.installed.map((font) => font.family))];
    if (result.installed.length > 0) setToast(`Installed ${String(result.installed.length)} font${result.installed.length === 1 ? '' : 's'}: ${names.join(', ')}${result.failed.length > 0 ? ` · failed: ${result.failed.join(', ')}` : ''}`);
    else setToast(result.failed.length > 0 ? `Could not install: ${result.failed.join(', ')}` : 'No font files were selected.');
    setRevision((value) => value + 1);
  }, []);

  useEffect(() => {
    window.localStorage.setItem('maipo-has-dirty-sessions', String(sessions.some((item) => item.dirty)));
  }, [sessions, revision]);

  useEffect(() => {
    let listening = true;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!sessions.some((item) => item.dirty)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const flush = () => {
      if (globalThis.document.visibilityState !== 'hidden') return;
      for (const item of sessions) if (item.dirty) void saveRecovery(item).catch(() => undefined);
    };
    const recoverAssets = () => {
      if (reloadRecoveryInFlight.current) return;
      reloadRecoveryInFlight.current = true;
      void prepareRecoveryReload(() => sessionsRef.current).then(async (ticket) => {
        if (!listening) return;
        if (!ticket.isCurrent()) throw new Error('Documents changed during recovery.');
        if (ticket.dirtyCount > 0 && !window.confirm('Maipo validated recovery snapshots for all edited documents. Reload now to repair updated application assets?')) return;
        // Confirmation may stay open while another tab changes recovery storage.
        // Recheck retained generations and current edits before the destructive reload.
        const verified = await ticket.verify();
        if (!listening) return;
        if (!verified || !ticket.isCurrent()) throw new Error('Recovery changed before reload.');
        window.location.reload();
      }).catch(() => { if (listening) setToast('Update reload paused: finish your edits and save your documents, then try again.'); })
        .finally(() => { reloadRecoveryInFlight.current = false; });
    };
    window.addEventListener('beforeunload', beforeUnload);
    globalThis.document.addEventListener('visibilitychange', flush);
    window.addEventListener('maipo-asset-recovery-required', recoverAssets);
    return () => {
      listening = false;
      window.removeEventListener('beforeunload', beforeUnload);
      globalThis.document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('maipo-asset-recovery-required', recoverAssets);
    };
  }, [sessions]);

  useEffect(() => {
    let listening = true;
    const stop = sessions.map(item => watchRecoveryAutosave(item, {
      onSaved: () => { void listAllRecoveries().then(items => { if (listening) setRecoveries(items); }).catch(() => undefined); },
      onFailure: () => { if (listening) setToast('Autosave could not update. Your in-memory document is unchanged.'); },
    }));
    return () => { listening = false; stop.forEach(dispose => dispose()); };
  }, [sessions]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 4_500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const addSession = useCallback((nextDocument: EditorDocument): EditorSession => {
    const next = session(nextDocument);
    setSessions((current) => {
      setActiveId(next.sessionId);
      return [...current, next];
    });
    return next;
  }, []);

  const openCadenciaFile = useCallback(async (file: File) => {
    const result = await registry.read(file);
    return addSession(result.document);
  }, [registry, addSession]);

  const openFiles = useCallback(async (files: File[]) => {
    for (const file of files) {
      const controller = new AbortController();
      setProgress({ message: `Opening ${file.name}`, value: 0.02, controller });
      try {
        const result = await registry.read(file, {
          signal: controller.signal,
          onProgress: (item) => setProgress({ message: item.message, value: item.progress, controller }),
        });
        addSession(result.document);
        if (result.document.importReport) setReport(result.document.importReport);
      } catch (reason) {
        if (!(reason instanceof DOMException && reason.name === 'AbortError')) setToast(reason instanceof Error ? reason.message : `Could not open ${file.name}.`);
      } finally {
        setProgress(null);
      }
    }
  }, [addSession, registry]);

  const placeImage = async (file: File) => {
    if (!active) return;
    const target = active;
    const controller = new AbortController();
    setProgress({ message: `Placing ${file.name}`, value: 0.2, controller });
    try {
      const binding = target.captureRevision();
      const { placeImageSource } = await import('../public-format/place-image-source');
      if (controller.signal.aborted) throw new DOMException('Cancelled.', 'AbortError');
      if (!target.isCurrent(binding)) throw new Error('The document changed. Place the image again.');
      const result = await placeImageSource(target, file, controller.signal);
      if (result.outcome === 'rejected') throw new Error(result.reason);
      setDockFamily('build');
      setRightTab('layers');
      setToast(`Placed ${file.name}. Original source preserved.`);
    } catch (reason) {
      if (!controller.signal.aborted) setToast(reason instanceof Error ? reason.message : 'Could not place this image.');
    } finally { setProgress(null); }
  };

  const addImageLayers = async (files: File[]) => {
    if (!document || !history) return;
    let placed = 0;
    for (const [index, file] of files.entries()) {
      try {
        const bitmap = await createImageBitmap(file);
        try {
          const canvas = globalThis.document.createElement('canvas');
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('Canvas 2D is unavailable.');
          context.drawImage(bitmap, 0, 0);
          const image = context.getImageData(0, 0, bitmap.width, bitmap.height);
          const layer = createRasterLayer(file.name.replace(/\.[a-z0-9]+$/i, '') || file.name, image.width, image.height);
          layer.raster = RasterTileStore.fromImageData(image);
          layer.transform = [1, 0, 0, 1, Math.round((document.width - image.width) / 2) + index * 24, Math.round((document.height - image.height) / 2) + index * 24];
          history.execute(document, new AddLayerCommand(layer));
          placed += 1;
        } finally {
          bitmap.close();
        }
      } catch {
        // reported through the summary toast below
      }
    }
    setToast(placed > 0 ? `Added ${String(placed)} layer${placed === 1 ? '' : 's'} to “${document.name}”.` : 'None of the dropped files could be added as layers.');
  };

  const dropFiles = async (files: File[], side: 'layer' | 'open') => {
    const fontFiles = files.filter((file) => /\.(ttf|otf|woff2?)$/i.test(file.name));
    const rest = files.filter((file) => !fontFiles.includes(file));
    if (fontFiles.length > 0) await installFonts(fontFiles);
    if (rest.length === 0) return;
    if (side === 'layer' && document) {
      // SVG's MIME type starts with "image/", so without excluding it here a
      // dropped SVG would be rasterised through createImageBitmap and arrive as
      // a flat bitmap — the exact opposite of importing it as editable paths.
      const isSvg = (file: File) => /\.svg$/i.test(file.name) || file.type === 'image/svg+xml';
      const imageFiles = rest.filter((file) => file.type.startsWith('image/') && !file.type.includes('photoshop') && !isSvg(file));
      const documentFiles = rest.filter((file) => !imageFiles.includes(file));
      if (imageFiles.length > 0) await addImageLayers(imageFiles);
      // Layered formats always open as their own documents.
      if (documentFiles.length > 0) await openFiles(documentFiles);
      return;
    }
    await openFiles(rest);
  };

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const images = [...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith('image/'));
      if (images.length) {
        event.preventDefault();
        void openFiles(images.map((file, index) => new File([file], file.name || `Clipboard ${index + 1}.png`, { type: file.type })));
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [openFiles]);

  const save = useCallback(async (saveAs = false) => {
    if (!document) return;
    const controller = new AbortController();
    setProgress({ message: 'Packing project tiles', value: 0.2, controller });
    try {
      if (!active) return;
      const result = await saveMaipoProject(active, controller.signal, saveAs);
      setRevision((value) => value + 1);
      setToast(result.durable ? 'Maipo project saved.' : 'Project copy downloaded. Browser storage is not confirmed.');
    } catch (reason) {
      if (!(reason instanceof DOMException && reason.name === 'AbortError')) setToast(reason instanceof Error ? reason.message : 'Save failed.');
    } finally {
      setProgress(null);
    }
  }, [active, document]);

  const exportImage = useCallback(async (format: 'png' | 'jpeg') => {
    if (!document) return;
    setToast(`Encoding ${format.toUpperCase()}…`);
    try {
      await exportFlattened(document, exportRenderer, format);
      setToast(`${format.toUpperCase()} exported.`);
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : 'Export failed.');
    }
  }, [document, exportRenderer]);

  const exportPublic = useCallback(async (format: 'json' | 'package') => {
    if (!document) return;
    const controller = new AbortController();
    setProgress({ message: 'Preparing public scene', value: 0.2, controller });
    try {
      const notices = await exportPublicScene(document, format, controller.signal);
      setToast(`Public scene exported.${notices.length ? ` ${notices.join(' ')}` : ''}`);
    } catch (reason) {
      if (!controller.signal.aborted) setToast(reason instanceof Error ? reason.message : 'Public scene export failed.');
    } finally {
      setProgress(null);
    }
  }, [document]);

  const exportAllImages = useCallback(async () => {
    setToast(`Packing ${sessions.length} PNG${sessions.length === 1 ? '' : 's'}…`);
    try {
      const count = await exportFlattenedZip(sessions.map((item) => item.document), exportRenderer, 'maipo-export');
      setToast(`Exported ${count} PNG${count === 1 ? '' : 's'} as one ZIP.`);
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : 'Export failed.');
    }
  }, [sessions, exportRenderer]);

  const removeDocument = (id: string, preferredId?: string) => {
    const item = sessions.find((candidate) => candidate.sessionId === id);
    sourceEdits.current.delete(id);
    for (const [key, edit] of sourceEdits.current) if (edit.target === item) sourceEdits.current.delete(key);
    item?.dispose();
    setSessions((current) => {
      const next = current.filter((candidate) => candidate.sessionId !== id);
      if (next.length === 0) {
        const replacement = session(initialDocument());
        setActiveId(replacement.sessionId);
        return [replacement];
      }
      if (preferredId && next.some(candidate => candidate.sessionId === preferredId)) setActiveId(preferredId);
      else if (id === activeId) setActiveId(next[Math.max(0, current.findIndex((candidate) => candidate.sessionId === id) - 1)]?.sessionId ?? next[0]?.sessionId ?? '');
      return next;
    });
  };

  agentSessionActions.current = { add: addSession, remove: removeDocument };

  const closeDocument = (id: string) => {
    const item = sessions.find(candidate => candidate.sessionId === id);
    if (item?.dirty && !window.confirm(`Close “${item.document.name}” with unsaved revisions? A recovery snapshot may be available.`)) return;
    removeDocument(id);
  };

  const editImagePixels = async (layerId: string) => {
    if (!active) return;
    const target = active;
    try {
      const binding = target.captureRevision();
      const { beginImageSourceEdit } = await import('../public-format/image-source-edit');
      if (!target.isCurrent(binding)) throw new Error('The document changed. Start the source edit again.');
      const edit = beginImageSourceEdit(target, layerId);
      const working = addSession(edit.document);
      sourceEdits.current.set(working.sessionId, edit);
      setDockFamily('build'); setRightTab('layers'); setTool('brush');
    } catch (reason) { setToast(reason instanceof Error ? reason.message : 'Could not edit this image.'); }
  };
  const finishSourceEdit = (working: EditorSession, edit?: ImageSourceEdit) => {
    removeDocument(working.sessionId, edit?.target.sessionId);
    void deleteRecovery(working.sessionId).catch(() => setToast('The source tab closed, but its recovery snapshot could not be removed.'));
  };
  const applySourceEdit = async () => {
    if (!active) return;
    const working = active;
    const edit = sourceEdits.current.get(working.sessionId);
    if (!edit) return;
    const controller = new AbortController();
    setProgress({ message: 'Applying source pixels', value: 0.2, controller });
    const renderer = new Canvas2DRenderer();
    try {
      const binding = working.captureRevision();
      const { applyImageSourceEdit } = await import('../public-format/image-source-edit');
      if (!working.isCurrent(binding)) throw new Error('The source document changed. Apply again.');
      const canvas = renderer.flatten(working.document);
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not read source pixels.');
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      const result = await applyImageSourceEdit(edit, working, { width: pixels.width, height: pixels.height, data: pixels.data, colorSpace: 'srgb', alpha: 'straight' }, controller.signal);
      if (result.outcome === 'rejected') throw new Error(result.reason);
      finishSourceEdit(working, edit);
      setToast('Source pixels applied as PNG. Original frame preserved.');
    } catch (reason) {
      if (!controller.signal.aborted) setToast(reason instanceof Error ? reason.message : 'Could not apply source pixels.');
    } finally { renderer.dispose(); setProgress(null); }
  };

  const runLayerCopy = async (message: string, work: (signal: AbortSignal) => Promise<CommandExecution>, complete: string) => {
    const controller = new AbortController();
    setProgress({ message, value: 0.2, controller });
    try {
      const result = await work(controller.signal);
      setToast(result.outcome === 'rejected' ? result.reason : result.outcome === 'changed' ? complete : 'No layer was added.');
    } catch (reason) {
      if (!controller.signal.aborted) setToast(reason instanceof Error ? reason.message : 'Layer copy failed.');
    } finally { setProgress(current => current?.controller === controller ? null : current); }
  };

  const duplicateLayer = () => {
    if (!active || !document) return;
    const id = document.activeLayerIds[0]; if (!id) return;
    void runLayerCopy('Duplicating layer', signal => duplicateLayerInSession(active, id, { signal }), 'Layer duplicated.');
  };

  const addBlackWhiteAdjustment = () => {
    if (!document || !history) return;
    const layer = createAdjustmentLayer('Black & White');
    layer.adjustment.blackWhite = { ...DEFAULT_BLACK_WHITE, enabled: true };
    history.execute(document, new AddLayerCommand(layer));
    setRightTab('layers');
  };

  const invertActiveLayer = () => {
    if (!document || !history) return;
    const id = document.activeLayerIds[0];
    const layer = id ? findLayer(document.root, id) : undefined;
    if (!layer || layer.kind !== 'raster') {
      setToast('Select a raster layer to invert its colors (adjustment layers offer non-destructive invert).');
      return;
    }
    const result = runCommand(active, 'pixels.invert', { layer: layer.id });
    if (result.status === 'rejected') setToast(result.message);
  };

  const copyActiveLayer = () => {
    if (!document) return;
    const id = document.activeLayerIds[0];
    const layer = id ? findLayer(document.root, id) : undefined;
    if (!layer) { setToast('Select a layer to copy.'); return; }
    try {
      setLayerClipboard(captureLayerClipboard(document, layer));
      setToast(`Copied layer “${layer.name}”.`);
    } catch (reason) { setToast(reason instanceof Error ? reason.message : 'Cannot copy layer resources.'); }
  };

  const pasteLayer = async () => {
    if (!active || !document || !layerClipboard) return;
    const placement = vectorDrawPlacement(document, vectorDrawMode);
    await runLayerCopy('Preparing pasted layer', signal => pasteLayerClipboard(layerClipboard, active,
      { parentId: placement.parentId, index: placement.index, clipping: placement.clipping,
        transform: transformForPlacement(layerClipboard.layer.transform, placement), signal }),
    `Pasted layer “${layerClipboard.layer.name}” into “${document.name}”.`);
  };

  const copyLayerToSession = async (layerId: string, targetSessionId: string) => {
    if (!document) return;
    const layer = findLayer(document.root, layerId);
    const target = sessions.find(item => item.sessionId === targetSessionId);
    if (!layer || !target || target.sessionId === active?.sessionId) return;
    try {
      const clipboard = captureLayerClipboard(document, layer);
      await runLayerCopy('Preparing copied layer', signal => pasteLayerClipboard(clipboard, target, { signal }),
        `Copied layer “${layer.name}” to “${target.document.name}”.`);
    } catch (reason) { setToast(reason instanceof Error ? reason.message : 'Cannot copy layer resources.'); }
  };

  const cropToSelection = () => {
    if (!document || !history) return;
    const bounds = selectionBounds(document.selection, { x: 0, y: 0, width: document.width, height: document.height });
    if (!bounds) return;
    runCommand(active, 'document.crop', { rect: bounds });
  };

  const fillForeground = () => {
    if (!document || !history || maskEditingLayerId) return;
    const layer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
    if (layer?.kind === 'raster') history.execute(document, new FillRasterCommand(layer.id, { type: 'solid', color: brush.color }));
  };

  const contentAwareFill = () => {
    if (!document || !history) return;
    const layer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
    if (!layer || layer.kind !== 'raster') {
      setToast('Select a raster layer before using content-aware fill.');
      return;
    }
    const result = history.execute(document, new ContentAwareFillCommand(layer.id));
    if (result.outcome === 'rejected') setToast(result.reason);
  };

  const activeLayer = document?.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
  // Object selection by criteria. Traced artwork arrives as hundreds of separate
  // objects, so picking them off one click at a time is not an option.
  const activeVectorObjects = activeLayer?.kind === 'vector' ? activeLayer.vector.objects : null;
  const objectSelectionIds = vectorObjectSelection?.layerId === activeLayer?.id ? vectorObjectSelection?.objectIds ?? [] : [];
  const applyObjectSelection = (objectIds: string[]) => {
    if (!activeLayer || activeLayer.kind !== 'vector' || objectIds.length === 0) return;
    setVectorObjectSelection({ layerId: activeLayer.id, objectIds, scope: 'object' });
  };
  // Exact contact, as opposed to grow's "reads as one figure". Only traced
  // artwork can answer it — the adjacency was recorded when the trace ran — so
  // the command is offered only when the layer carries that table.
  const traceTopology = activeLayer?.kind === 'vector' ? readTraceTopology(activeLayer) : null;
  const selectTouchingObjects = () => {
    if (!activeVectorObjects || !traceTopology) return;
    const next = selectTouching(activeVectorObjects, objectSelectionIds, traceTopology);
    applyObjectSelection(next);
    setToast(`Selected ${next.length} object${next.length === 1 ? '' : 's'} sharing a boundary.`);
  };

  const selectBySwatch = (swatchId: string) => {
    if (!activeVectorObjects) return;
    const next = withSwatch(activeVectorObjects, swatchId);
    if (next.length === 0) { setToast('Nothing on this layer uses that colour.'); return; }
    applyObjectSelection(next);
    setToast(`Selected ${next.length} object${next.length === 1 ? '' : 's'} using that colour.`);
  };

  const groupedSelection = activeLayer?.kind === 'vector' && objectSelectionIds.length > 0 && hasGroupedObject(activeLayer, objectSelectionIds);

  const groupObjects = () => {
    if (!document || !history || activeLayer?.kind !== 'vector') return;
    const result = history.execute(document, new TagObjectGroupCommand(activeLayer.id, objectSelectionIds, true));
    if (result.outcome === 'rejected') setToast(result.reason);
    else setToast(`Grouped ${objectSelectionIds.length} objects. Double-click one to work inside the group.`);
  };
  const ungroupObjects = () => {
    if (!document || !history || activeLayer?.kind !== 'vector') return;
    const result = history.execute(document, new TagObjectGroupCommand(activeLayer.id, objectSelectionIds, false));
    if (result.outcome === 'rejected') setToast(result.reason);
    else { setGroupEntry(null); setToast('Ungrouped.'); }
  };

  const selectSameColour = () => {
    if (!activeVectorObjects) return;
    const next = sameFill(activeVectorObjects, objectSelectionIds);
    applyObjectSelection(next);
    setToast(`Selected ${next.length} object${next.length === 1 ? '' : 's'} sharing that colour.`);
  };
  const growObjectSelection = () => {
    if (!activeVectorObjects) return;
    const next = growSelection(activeVectorObjects, objectSelectionIds);
    applyObjectSelection(next);
    setToast(`Grew the selection to ${next.length} object${next.length === 1 ? '' : 's'}.`);
  };

  const traceActiveLayer = () => {
    if (!document || !history) return;
    const layer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
    if (!layer || layer.kind !== 'raster') {
      setToast('Select a raster layer before using Image Trace.');
      return;
    }
    const outcome = runCommand(active, 'vector.imageTrace', { layer: layer.id, threshold: 128, invert: false });
    if (outcome.status === 'rejected') {
      setToast(outcome.message);
      return;
    }
    setToast('Created an editable black-and-white Image Trace above the source.');
  };

  const convertToSmartObject = () => {
    if (!document || !history) return;
    const result = history.execute(document, new ConvertToSmartObjectCommand(document.activeLayerIds));
    if (result.outcome === 'rejected') setToast(result.reason);
    else if (result.outcome === 'changed') setToast('Embedded source layers in a losslessly transformable Smart Object.');
  };

  const editSmartObjectContents = () => {
    if (!document || !history) return;
    const layer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
    if (!isSmartObject(layer)) return;
    runCommand(active, 'layer.setExpanded', { layer: layer.id, expanded: true });
    setToast('Smart Object contents expanded in the Layers panel. Edits update the embedded source.');
  };

  const rasterizeSmartObject = () => {
    if (!document || !history) return;
    const layer = document.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
    if (!isSmartObject(layer)) return;
    try {
      const result = rasterizeSmartObjectInSession(active, layer.id, exportRenderer);
      if (result.outcome === 'rejected') setToast(result.reason);
      else if (result.outcome === 'changed') setToast('Smart Object rasterized. Undo restores the embedded source.');
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : 'Smart Object rasterization failed.');
    }
  };

  const startActionRecording = () => {
    if (!document) return;
    const name = window.prompt('Action name', `Action ${documentActions(document).length + 1}`)?.trim();
    if (!name) return;
    const draft = { name, steps: [] as ActionStep[] };
    recordingActionRef.current = draft;
    setRecordingActionName(name);
    setRecordedStepCount(0);
    setToast(`Recording “${name}”. Committed edits will become action steps.`);
  };

  const stopActionRecording = () => {
    const draft = recordingActionRef.current;
    recordingActionRef.current = null;
    setRecordingActionName(null);
    setRecordedStepCount(0);
    if (!draft || !document || !history) return;
    if (!draft.steps.length) {
      setToast('Recording discarded because it had no committed steps.');
      return;
    }
    history.execute(document, new AddActionCommand(newRecordedAction(draft.name, draft.steps)));
    setToast(`Saved action “${draft.name}” with ${draft.steps.length} step${draft.steps.length === 1 ? '' : 's'}.`);
  };

  const actionIsOffered = (id: EditorActionId) => offeredIn(EDITOR_ACTION_MEDIA[id], mediaMode, false);
  const firstHiddenActionStep = (steps: readonly ActionStep[]) => steps.findIndex((step) => !offeredIn(actionStepMedia(step), mediaMode, false));

  const playRecordedAction = async (action: ReturnType<typeof documentActions>[number], batch = false) => {
    if (!active || replayingActionRef.current || progress) return;
    const hiddenStep = firstHiddenActionStep(action.steps);
    if (hiddenStep >= 0) {
      setToast(`“${action.name}” was not played: step ${hiddenStep + 1} is hidden by the ${modeLabel(mediaMode)} workspace.`);
      return;
    }
    const controller = new AbortController();
    replayingActionRef.current = true;
    setProgress({ message: `Playing “${action.name}”`, value: 0.2, controller });
    try {
      if (batch) {
        const results = await replayActionBatch(sessions, action, { signal: controller.signal });
        const completed = [...results.values()].filter(result => result.completed).length;
        const failure = [...results.values()].find(result => !result.completed);
        setToast(`Played “${action.name}” in ${completed}/${results.size} open documents.${failure ? ` Stopped: ${failure.reason ?? 'failed'} Completed steps can be undone.` : ''}`);
      } else {
        const result = await replayAction(active, action, { signal: controller.signal });
        setToast(result.completed ? `Played “${action.name}”.` : `“${action.name}” stopped at step ${(result.failedStepIndex ?? 0) + 1}: ${result.reason ?? 'failed'} Completed steps can be undone.`);
      }
    } catch (reason) {
      setToast(reason instanceof Error ? reason.message : 'Action replay failed.');
    } finally {
      replayingActionRef.current = false;
      setProgress(current => current?.controller === controller ? null : current);
    }
  };

  const selectedLayer = document?.activeLayerIds[0] ? findLayer(document.root, document.activeLayerIds[0]) : undefined;
  const workerStats = analysisDiagnostics();

  const actions: Action[] = document && history ? [
    { id: 'new', label: 'New document', hint: '⌘N', run: () => setNewDialog(true) },
    { id: 'open', label: 'Open image or project', hint: '⌘O', run: () => fileInput.current?.click() },
    { id: 'edit-portable-text', label: 'Edit portable text on canvas', disabled: selectedLayer?.kind !== 'text' || selectedLayer.text.schemaVersion !== 2 || selectedLayer.locked, run: () => { if (active && selectedLayer?.kind === 'text') setPortableCanvasEdit({ session: active, layer: selectedLayer, point: null }); } },
    { id: 'new-portable-text', label: 'New portable text…', run: () => setPortableTextSession(active ?? null) },
    { id: 'place-image', label: 'Place image', run: () => placeImageInput.current?.click() },
    { id: 'save', label: 'Save Maipo project', hint: '⌘S', run: () => void save() },
    { id: 'save-as', label: 'Save Maipo project as…', hint: '⇧⌘S', run: () => void save(true) },
    { id: 'public-json', label: hasNativePortableText(document.root) ? 'Export public scene JSON (portable draft)' : 'Export public scene JSON', run: () => void exportPublic('json') },
    { id: 'public-package', label: hasNativePortableText(document.root) ? 'Export public Maipo package (portable draft)' : 'Export public Maipo package', run: () => void exportPublic('package') },
    { id: 'png', label: 'Export flattened PNG', run: () => void exportImage('png') },
    { id: 'jpeg', label: 'Export flattened JPEG', run: () => void exportImage('jpeg') },
    { id: 'undo', label: history.state().undoLabel ? `Undo ${history.state().undoLabel}` : 'Undo', hint: '⌘Z', run: () => history.undo(document), disabled: !history.state().canUndo },
    { id: 'redo', label: history.state().redoLabel ? `Redo ${history.state().redoLabel}` : 'Redo', hint: '⇧⌘Z', run: () => history.redo(document), disabled: !history.state().canRedo },
    { id: 'duplicate', label: 'Duplicate active layer', hint: '⌘J', run: duplicateLayer, disabled: document.activeLayerIds.length === 0 },
    { id: 'convert-smart-object', label: 'Convert to Smart Object', run: convertToSmartObject, disabled: document.activeLayerIds.length === 0 || document.activeLayerIds.some((id) => isSmartObject(findLayer(document.root, id))) },
    { id: 'edit-smart-object', label: 'Edit Smart Object contents', run: editSmartObjectContents, disabled: !isSmartObject(selectedLayer) },
    { id: 'rasterize-smart-object', label: 'Rasterize Smart Object', run: rasterizeSmartObject, disabled: !isSmartObject(selectedLayer) },
    { id: 'record-action', label: recordingActionName ? `Stop recording “${recordingActionName}”` : 'Record new action', run: recordingActionName ? stopActionRecording : startActionRecording },
    { id: 'fill-foreground', label: 'Fill selection with foreground', run: fillForeground, disabled: selectedLayer?.kind !== 'raster' || Boolean(maskEditingLayerId) },
    { id: 'content-aware-fill', label: 'Content-aware fill (classic)', run: contentAwareFill, disabled: selectedLayer?.kind !== 'raster' || document.selection.regions.length === 0 || document.selection.inverted },
    { id: 'liquify', label: 'Liquify', run: () => setDeformDialog('liquify'), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'puppet-warp', label: 'Puppet Warp', run: () => setDeformDialog('puppet'), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'warp-perspective', label: 'Free Transform · Warp / Perspective', run: () => setDeformDialog('warp-perspective'), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'apply-image', label: 'Apply Image / Calculations', run: () => setApplyImageDialog(true), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'color-range', label: 'Select Color Range', run: () => setColorRangeDialog(true), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'vanishing-point', label: 'Perspective Grid / Vanishing Point', run: () => setVanishingPointDialog(true) },
    { id: 'batch-process', label: 'Batch Processing / Image Processor', run: () => setBatchProcessDialog(true) },
    { id: 'data-driven', label: 'Variables & Data-Driven Graphics', run: () => setDataDrivenDialog(true) },
    { id: 'tool-presets', label: 'Tool Presets', run: () => setToolPresetsDialog(true) },
    { id: 'high-pass', label: 'High Pass Filter', run: () => setHighPassDialog(true), disabled: selectedLayer?.kind !== 'raster' && !isSmartObject(selectedLayer) },
    { id: 'displace', label: 'Displace Filter', run: () => setDisplaceDialog(true), disabled: selectedLayer?.kind !== 'raster' && !isSmartObject(selectedLayer) },
    { id: 'match-color', label: 'Match Color', run: () => setMatchColorDialog(true), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'luminosity-masks', label: 'Luminosity Masks', run: () => setLuminosityMasksDialog(true), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'content-aware-scale', label: 'Content-Aware Scale', run: () => setContentAwareScaleDialog(true), disabled: selectedLayer?.kind !== 'raster' },
    { id: 'black-white-adjustment', label: 'Add Black & White Adjustment Layer', run: addBlackWhiteAdjustment },
    { id: 'resize-image', label: 'Resize image', run: () => setGeometryDialog('image') },
    { id: 'invert-layer-colors', label: 'Invert layer colors', run: invertActiveLayer },
    { id: 'resize-canvas', label: 'Resize canvas', run: () => setGeometryDialog('canvas') },
    { id: 'crop-selection', label: 'Crop to selection', run: cropToSelection, disabled: !hasSelection(document.selection) || !selectionBounds(document.selection, { x: 0, y: 0, width: document.width, height: document.height }) },
    { id: 'rotate-cw', label: 'Rotate document 90° clockwise', run: () => runCommand(active, 'document.rotate', { direction: 'clockwise' }) },
    { id: 'rotate-ccw', label: 'Rotate document 90° counterclockwise', run: () => runCommand(active, 'document.rotate', { direction: 'counterclockwise' }) },
    { id: 'flip-document-horizontal', label: 'Flip document horizontally', run: () => runCommand(active, 'document.flip', { axis: 'horizontal' }) },
    { id: 'flip-document-vertical', label: 'Flip document vertically', run: () => runCommand(active, 'document.flip', { axis: 'vertical' }) },
    { id: 'add-guide', label: 'Add guide', run: () => setGuideDialog(true) },
    { id: 'new-guide-layout', label: 'New Guide Layout', run: () => setGuideLayoutDialog(true) },
    { id: 'clear-guides', label: 'Clear guides', run: () => runCommand(active, 'guides.clear', {}), disabled: (document.guides?.length ?? 0) === 0 },
    { id: 'toggle-grid', label: showGrid ? 'Hide grid' : 'Show grid', run: () => setShowGrid((value) => !value) },
    { id: 'toggle-snap', label: snapEnabled ? 'Disable snapping' : 'Enable snapping', run: () => setSnapEnabled((value) => !value) },
    { id: 'shortcuts', label: 'Customize tool shortcuts', run: () => setShortcutDialog(true) },
    { id: 'select-all', label: 'Select all pixels', hint: '⌘A', run: () => history.execute(document, SelectionCommand.all()) },
    { id: 'clear-selection', label: 'Clear selection', hint: '⌘D', run: () => history.execute(document, SelectionCommand.clear()) },
    { id: 'invert-selection', label: 'Invert selection', hint: '⇧⌘I', run: () => history.execute(document, SelectionCommand.invert()) },
    { id: 'select-same-fill', label: 'Select objects with the same fill colour', run: selectSameColour, disabled: !activeVectorObjects || objectSelectionIds.length === 0 },
    { id: 'grow-object-selection', label: 'Grow selection to touching objects', run: growObjectSelection, disabled: !activeVectorObjects || objectSelectionIds.length === 0 },
    { id: 'select-touching-regions', label: 'Select regions that touch exactly', run: selectTouchingObjects, disabled: !traceTopology || objectSelectionIds.length === 0 },
    { id: 'group-objects', label: 'Group objects', hint: '⌘G', run: groupObjects, disabled: !activeVectorObjects || objectSelectionIds.length < 2 },
    { id: 'ungroup-objects', label: 'Ungroup objects', hint: '⇧⌘G', run: ungroupObjects, disabled: !activeVectorObjects || !groupedSelection },
    { id: 'select-all-objects', label: 'Select all vector objects', run: () => applyObjectSelection(selectAllObjects(activeVectorObjects ?? [])), disabled: !activeVectorObjects },
    { id: 'colour-trace', label: 'Colour Trace…', run: () => setColourTraceDialog(true), disabled: activeLayer?.kind !== 'raster' },
    { id: 'recover', label: 'Open recovery snapshots', run: () => setRecoveryDialog(true), disabled: recoveries.length === 0 },
  ] : [];
  const visibleAddonActions: PaletteAction[] = document && history ? addonCommands
    .filter(({ command }) => command.media ? offeredIn(command.media, mediaMode, false) : mediaMode === 'all')
    .map(({ addon, command }) => ({
      id: `addon:${addon.id}:${command.id}`,
      label: `${command.label} · ${addon.name}`,
      run: () => {
        const boundSession = active;
        const context = createAddonContext(addon, () => {
          if (activeSessionRef.current !== boundSession) throw new Error('The active document changed; the add-on command was stopped.');
          return { session: boundSession, document, history, onNotify: setToast };
        });
        void Promise.resolve(command.run(context)).catch((reason: unknown) => {
          setToast(reason instanceof Error ? reason.message : `${command.label} failed.`);
        });
      },
    })) : [];
  const visibleActions: PaletteAction[] = [...actions.filter((action) => actionIsOffered(action.id)), ...visibleAddonActions];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (replayingActionRef.current) return;
      if (event.key === ' ') {
        if (!isTypingTarget(event.target)) {
          event.preventDefault();
          setTemporaryHand(true);
        }
        return;
      }
      if (event.key === 'Escape') {
        const hadDismissibleSurface = Boolean(
          menuBarRef.current?.querySelector('details[open]')
          || paletteOpen
          || newDialog
          || portableTextSession
          || portableCanvasEdit
          || geometryDialog
          || deformDialog
          || applyImageDialog
          || colorRangeDialog
          || colourTraceDialog
          || vanishingPointDialog
          || batchProcessDialog
          || dataDrivenDialog
          || highPassDialog
          || displaceDialog
          || matchColorDialog
          || luminosityMasksDialog
          || contentAwareScaleDialog
          || vectorDrawMode !== 'normal'
          || guideDialog
          || guideLayoutDialog
          || shortcutDialog
          || toolPresetsDialog
          || report
          || recoveryDialog,
        );
        closeMenus();
        setPaletteOpen(false);
        setNewDialog(false);
        setPortableTextSession(null);
        setPortableCanvasEdit(null);
        setGeometryDialog(null);
        setDeformDialog(null);
        setApplyImageDialog(false);
        setColorRangeDialog(false);
        setVanishingPointDialog(false);
        setBatchProcessDialog(false);
        setDataDrivenDialog(false);
        setHighPassDialog(false);
        setDisplaceDialog(false);
        setMatchColorDialog(false);
        setLuminosityMasksDialog(false);
        setContentAwareScaleDialog(false);
        setVectorDrawMode('normal');
        setGuideDialog(false);
        setGuideLayoutDialog(false);
        setShortcutDialog(false);
        setToolPresetsDialog(false);
        setReport(null);
        setRecoveryDialog(false);
        if (!hadDismissibleSurface && vectorObjectSelection?.scope === 'node') {
          event.preventDefault();
          setVectorObjectSelection({ ...vectorObjectSelection, scope: 'object', nodeIds: [] });
          return;
        }
        if (!hadDismissibleSurface && groupEntry) {
          event.preventDefault();
          // Leaving the group re-selects it whole, which is where you were
          // before you entered.
          const layer = document ? findLayer(document.root, groupEntry.layerId) : undefined;
          if (layer?.kind === 'vector' && vectorObjectSelection?.layerId === layer.id) {
            setVectorObjectSelection({ ...vectorObjectSelection, objectIds: expandToGroups(layer, vectorObjectSelection.objectIds), scope: 'object' });
          }
          setGroupEntry(null);
          return;
        }
        if (!hadDismissibleSurface && isolationScope) {
          event.preventDefault();
          setIsolationScope(null);
          return;
        }
        if (!hadDismissibleSurface && vectorObjectSelection) {
          event.preventDefault();
          setVectorObjectSelection(null);
          return;
        }
        if (!hadDismissibleSurface && document && history && offeredIn(EDITOR_ACTION_MEDIA['clear-selection'], mediaMode, false) && (document.selection.inverted || document.selection.regions.length > 0)) {
          event.preventDefault();
          history.execute(document, SelectionCommand.clear());
        }
        return;
      }
      if (isTypingTarget(event.target) || !document || !history) return;
      if (event.key === 'Tab') {
        const activeLayerId = vectorObjectSelection?.layerId ?? document.activeLayerIds[0];
        const layer = activeLayerId ? findLayer(document.root, activeLayerId) : undefined;
        if (layer?.kind === 'vector' && mediaMode === 'raster') return;
        if (!vectorObjectSelection && layer?.kind === 'vector' && layer.vector.objects[0]) {
          event.preventDefault();
          setVectorObjectSelection({ layerId: layer.id, objectIds: [layer.vector.objects[0].id], scope: 'object' });
          return;
        }
        const object = layer?.kind === 'vector' && vectorObjectSelection ? layer.vector.objects.find((item) => item.id === vectorObjectSelection.objectIds[0]) : undefined;
        if (object?.type === 'path' && vectorObjectSelection) {
          event.preventDefault();
          setVectorObjectSelection({ ...vectorObjectSelection, scope: vectorObjectSelection.scope === 'node' ? 'object' : 'node', nodeIds: [] });
          return;
        }
      }
      const command = event.metaKey || event.ctrlKey;
      const rejectHiddenCommand = (id: EditorActionId, label: string) => {
        if (offeredIn(EDITOR_ACTION_MEDIA[id], mediaMode, false)) return false;
        setToast(`${label} is hidden by the ${modeLabel(mediaMode)} workspace and was not run.`);
        return true;
      };
      if (command && event.key.toLowerCase() === 'k') { event.preventDefault(); setPaletteOpen(true); return; }
      if (command && event.key.toLowerCase() === 'n') { event.preventDefault(); setNewDialog(true); return; }
      if (command && event.key.toLowerCase() === 'o') { event.preventDefault(); fileInput.current?.click(); return; }
      if (command && event.key.toLowerCase() === 's') { event.preventDefault(); void save(event.shiftKey); return; }
      if (command && event.key.toLowerCase() === 'z') { event.preventDefault(); if (event.shiftKey) history.redo(document); else history.undo(document); return; }
      if (command && event.key.toLowerCase() === 'a') { event.preventDefault(); if (!rejectHiddenCommand('select-all', 'Select all pixels')) history.execute(document, SelectionCommand.all()); return; }
      if (command && event.key.toLowerCase() === 'd') { event.preventDefault(); if (!rejectHiddenCommand('clear-selection', 'Clear pixel selection')) history.execute(document, SelectionCommand.clear()); return; }
      if (command && event.shiftKey && event.key.toLowerCase() === 'i') { event.preventDefault(); if (!rejectHiddenCommand('invert-selection', 'Invert pixel selection')) history.execute(document, SelectionCommand.invert()); return; }
      if (command && event.key.toLowerCase() === 'j') { event.preventDefault(); duplicateLayer(); return; }
      if (command && event.key.toLowerCase() === 'g') {
        event.preventDefault();
        const id: EditorActionId = event.shiftKey ? 'ungroup-objects' : 'group-objects';
        const label = event.shiftKey ? 'Ungroup objects' : 'Group objects';
        if (!rejectHiddenCommand(id, label)) {
          if (event.shiftKey) ungroupObjects(); else groupObjects();
        }
        return;
      }
      if (command && event.key.toLowerCase() === 'c' && !event.shiftKey && document.activeLayerIds.length > 0) { event.preventDefault(); copyActiveLayer(); return; }
      if (command && event.key.toLowerCase() === 'v' && layerClipboard) { event.preventDefault(); void pasteLayer(); return; }
      if (!command && event.key.toLowerCase() === 'q') {
        event.preventDefault();
        if (mediaMode === 'vector') {
          setToast('Quick Mask is hidden by the Vector workspace and was not activated.');
          setMediaAnnouncement('Quick Mask paints a pixel selection and is hidden by the Vector workspace.');
          return;
        }
        setQuickMaskSessions((current) => ({ ...current, [active.sessionId]: !current[active.sessionId] }));
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (vectorObjectSelection?.scope === 'node' && vectorObjectSelection.nodeIds?.length) {
          event.preventDefault();
          if (rejectHiddenCommand('select-all-objects', 'Delete vector path nodes')) return;
          const selection = vectorObjectSelection;
          const boundSession = active;
          void import('../vector/runtime').then(({ DeletePathNodesCommand }) => {
            if (activeSessionRef.current !== boundSession) return;
            history.execute(document, new DeletePathNodesCommand(selection.layerId, selection.objectIds[0] ?? '', selection.nodeIds ?? []));
            setVectorObjectSelection({ ...selection, nodeIds: [] });
          });
          return;
        }
        const selectedObjectId = vectorObjectSelection?.layerId === document.activeLayerIds[0] ? vectorObjectSelection?.objectIds[0] : undefined;
        if (selectedObjectId && vectorObjectSelection) {
          event.preventDefault();
          if (rejectHiddenCommand('select-all-objects', 'Delete vector object')) return;
          const selection = vectorObjectSelection;
          const boundSession = active;
          void import('../vector/runtime').then(({ RemoveVectorObjectCommand }) => {
            if (activeSessionRef.current !== boundSession) return;
            history.execute(document, new RemoveVectorObjectCommand(selection.layerId, selectedObjectId));
            setVectorObjectSelection(null);
          });
          return;
        }
        const id = document.activeLayerIds[0];
        const layer = id ? findLayer(document.root, id) : undefined;
        if (layer?.kind === 'raster' && hasSelection(document.selection)) {
          event.preventDefault();
          if (rejectHiddenCommand('clear-selection', 'Clear selected pixels')) return;
          history.execute(document, new ClearRasterSelectionCommand(layer.id));
          return;
        }
        if (id) runPanelOperation(active, removeLayer(id));
        return;
      }
      const matched = tools.find((candidate) => toolShortcuts[candidate.id].toLowerCase() === event.key.toLowerCase());
      if (matched) {
        event.preventDefault();
        if (!visibleIn(matched.media, mediaMode)) {
          setToast(`${matched.label} is hidden by the ${modeLabel(mediaMode)} workspace and was not activated.`);
          return;
        }
        const activeLayerId = document.activeLayerIds[0];
        const keyboardActiveLayer = activeLayerId ? findLayer(document.root, activeLayerId) : undefined;
        const unavailable = toolUnavailableReason(matched.id, keyboardActiveLayer, maskEditingLayerId);
        if (unavailable) setToast(unavailable);
        else if (!visibleIn(matched.media, mediaMode)) {
          setMediaAnnouncement(`${matched.label} is hidden by the ${modeLabel(mediaMode)} workspace. The shortcut stays bound but will not activate it.`);
        } else setTool(matched.id);
        return;
      }
      if (event.key === '0') setViewAction((value) => ({ id: value.id + 1, action: 'fit' }));
      if (event.key === '+' || event.key === '=') setViewAction((value) => ({ id: value.id + 1, action: 'in' }));
      if (event.key === '-') setViewAction((value) => ({ id: value.id + 1, action: 'out' }));
      if (event.key.toLowerCase() === 'd') {
        setBrush((value) => ({ ...value, color: [0, 0, 0, 255] }));
        setBackgroundColor('#ffffff');
      }
      if (event.key.toLowerCase() === 'x') {
        const foreground = `#${brush.color.slice(0, 3).map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
        const next = backgroundColor.match(/[a-f\d]{2}/gi)?.map((value) => Number.parseInt(value, 16)) ?? [255, 255, 255];
        setBrush((value) => ({ ...value, color: [next[0] ?? 255, next[1] ?? 255, next[2] ?? 255, 255] }));
        setBackgroundColor(foreground);
      }
    };
    const onKeyUp = (event: KeyboardEvent) => { if (event.key === ' ') setTemporaryHand(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
  // the omitted callbacks are
  // rebuilt every render; depending on them would tear down and rebind every global
  // shortcut on each keystroke. This list is the state the handlers actually read.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, history, save, exportImage, backgroundColor, brush.color, recoveries.length, toolShortcuts, mediaMode, paletteOpen, newDialog, portableTextSession, portableCanvasEdit, geometryDialog, deformDialog, applyImageDialog, colorRangeDialog, colourTraceDialog, groupEntry, vanishingPointDialog, batchProcessDialog, dataDrivenDialog, highPassDialog, displaceDialog, matchColorDialog, luminosityMasksDialog, contentAwareScaleDialog, vectorDrawMode, guideDialog, guideLayoutDialog, shortcutDialog, toolPresetsDialog, report, recoveryDialog, closeMenus, vectorObjectSelection, isolationScope, maskEditingLayerId, layerClipboard]);

  if (!document || !history) return null;
  const quickMaskActive = Boolean(quickMaskSessions[active.sessionId]);
  const visibleRecordedActions = documentActions(document).filter((action) => firstHiddenActionStep(action.steps) === -1);



  /**
   * Switching workspace changes no document content and writes no history
   * entry. It may replace the active tool and exit Quick Mask, and it announces
   * whichever of those happened — a control vanishing without explanation reads
   * as a bug rather than as a filter.
   */
  const switchMediaMode = (next: MediaMode) => {
    const current = tools.find((item) => item.id === tool) ?? tools[0]!;
    const activeAddonPanel = addonPanels.find(({ addon, panel }) => rightTab === `addon:${addon.id}:${panel.id}`);
    const hidesDockPanel = isEditorPanelId(rightTab)
      ? !offeredIn(EDITOR_PANEL_MEDIA[rightTab], next, false)
      : rightTab.startsWith('addon:')
        && (!activeAddonPanel || (activeAddonPanel.panel.media ? !offeredIn(activeAddonPanel.panel.media, next, false) : next !== 'all'));
    const dockOwnedFocus = globalThis.document.activeElement instanceof HTMLElement
      && Boolean(globalThis.document.activeElement.closest('.right-dock'));
    const outcome = planModeSwitch({
      next,
      activeTool: current.id,
      activeToolLabel: current.label,
      activeToolPolicy: current.media,
      fallbackTool: 'move',
      quickMaskActive,
    });
    setMediaMode(next);
    storeMediaMode(next);
    history.cancelLive(document);
    if (hidesDockPanel) {
      setDockFamily('build');
      setRightTab('layers');
    }
    if (outcome.toolChanged) setTool(outcome.tool as ToolId);
    if (outcome.exitsQuickMask) setQuickMaskSessions((value) => ({ ...value, [active.sessionId]: false }));
    const exitsMaskEditing = next === 'vector' && maskEditingLayerId !== null;
    const exitsVectorEditing = next === 'raster' && (vectorObjectSelection !== null || isolationScope !== null || groupEntry !== null || directTextEditingLayerId !== null);
    if (exitsMaskEditing) setMaskEditingLayerId(null);
    if (next === 'raster') {
      setVectorObjectSelection(null);
      setIsolationScope(null);
      setGroupEntry(null);
      setDirectTextEditingLayerId(null);
    }
    setTemporaryHand(false);
    setMediaAnnouncement(`${outcome.announcement}${exitsMaskEditing ? ' Raster mask editing was exited.' : ''}${exitsVectorEditing ? ' Vector object editing was exited.' : ''}${hidesDockPanel ? ' The active dock panel is hidden here, so the dock fell back to Layers.' : ''}`);
    if (hidesDockPanel && dockOwnedFocus) {
      window.requestAnimationFrame(() => globalThis.document.querySelector<HTMLElement>('.right-dock .panel-tabs [role="tab"][aria-selected="true"]')?.focus());
    } else if (outcome.toolChanged) {
      window.requestAnimationFrame(() => globalThis.document.querySelector<HTMLElement>('.tool-rail button.active')?.focus());
    }
  };

  const onMediaModeKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const current = MEDIA_MODES.indexOf(mediaMode);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (current - 1 + MEDIA_MODES.length) % MEDIA_MODES.length;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (current + 1) % MEDIA_MODES.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = MEDIA_MODES.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = MEDIA_MODES[nextIndex];
    if (!next) return;
    const group = event.currentTarget;
    switchMediaMode(next);
    window.requestAnimationFrame(() => group.querySelector<HTMLElement>(`[role="radio"][data-mode="${next}"]`)?.focus());
  };



  const channelView = channelViews[active.sessionId] ?? 'composite';
  const alphaChannelSelection = channelView.startsWith('alpha:')
    ? alphaChannels(document).find((channel) => channel.id === channelView.slice(6))?.selection ?? null
    : null;
  const sessionSnapshots = historySnapshots[active.sessionId] ?? [];
  const activeSnapshot = sessionSnapshots.find((snapshot) => snapshot.id === activeHistorySnapshots[active.sessionId]);
  const historyBrushSource = activeLayer?.kind === 'raster'
    && activeSnapshot?.layerId === activeLayer.id
    && activeSnapshot.raster.width === activeLayer.raster.width
    && activeSnapshot.raster.height === activeLayer.raster.height
    ? activeSnapshot.raster
    : null;
  const editingTarget = editingContext(activeLayer, maskEditingLayerId, vectorObjectSelection, directTextEditingLayerId);
  const toolBlockedReason = (quickMaskActive && (tool === 'brush' || tool === 'eraser') ? null : toolUnavailableReason(tool, activeLayer, maskEditingLayerId))
    ?? (tool === 'history-brush' && !historyBrushSource ? 'Choose a same-layer snapshot in the History panel before painting.' : null);
  const historyState = history.state();
  const historyCommands = history.timelineCommands();
  const revertHistoryEntry = (target: TransactionCommand) => {
    const outcome = revertTransaction(active, target);
    if (outcome.status === 'rejected') setToast(outcome.message);
  };
  const foregroundHex = `#${brush.color.slice(0, 3).map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
  const applyForeground = (color: string) => {
    const normalized = color.toLowerCase();
    const parts = normalized.match(/[a-f\d]{2}/gi)?.map((value) => Number.parseInt(value, 16)) ?? [0, 0, 0];
    setBrush((current) => ({ ...current, color: [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0, 255] }));
    setRecentColors((current) => [normalized, ...current.filter((item) => item !== normalized)].slice(0, 8));
  };
  const memory = estimateDocumentBytes(document);
  const documentArtboards = artboards(document);
  const documentSlices = slices(document);
  const canvasRegions = [...documentArtboards, ...documentSlices.map((slice) => ({ id: slice.id, name: slice.name, rect: slice.rect, background: 'transparent' as const }))];
  const exportMarkedAssets = (assets: readonly ExportAsset[]) => {
    void exportAssetArchive(document, exportRenderer, assets, `${document.name}-assets`)
      .then((count) => setToast(`Exported ${count} asset${count === 1 ? '' : 's'} as ZIP.`))
      .catch((reason: unknown) => setToast(reason instanceof Error ? reason.message : 'Asset export failed.'));
  };
  const exportArtboards = () => {
    void exportArtboardArchive(document, exportRenderer)
      .then((count) => setToast(`Exported ${count} artboard${count === 1 ? '' : 's'} as ZIP.`))
      .catch((reason: unknown) => setToast(reason instanceof Error ? reason.message : 'Artboard export failed.'));
  };
  const exportSlices = (selectedSlices: readonly Slice[]) => {
    void exportSliceArchive(document, exportRenderer, selectedSlices)
      .then((count) => setToast(`Exported ${count} slice${count === 1 ? '' : 's'} as ZIP.`))
      .catch((reason: unknown) => setToast(reason instanceof Error ? reason.message : 'Slice export failed.'));
  };
  const addNoteAt = (point: { x: number; y: number }) => {
    const notes = documentNotes(document);
    if (notes.length >= 100) {
      setToast('This document has reached the 100-note safety limit.');
      return;
    }
    runCommand(active, 'notes.add', { x: point.x, y: point.y }, undefined, 'Pin note');
    setRightTab('notes');
  };
  const takeHistorySnapshot = () => {
    if (activeLayer?.kind !== 'raster') {
      setToast('Select a raster layer before taking a history snapshot.');
      return;
    }
    const existingBytes = sessionSnapshots.reduce((sum, snapshot) => sum + snapshot.raster.byteLength(), 0);
    if (existingBytes + activeLayer.raster.byteLength() > 128 * 1024 * 1024) {
      setToast('History snapshots are limited to 128 MiB per document.');
      return;
    }
    const snapshot: RasterHistorySnapshot = {
      id: createId('snapshot'),
      name: `Snapshot ${sessionSnapshots.length + 1}`,
      layerId: activeLayer.id,
      layerName: activeLayer.name,
      raster: activeLayer.raster.clone(),
    };
    setHistorySnapshots((current) => ({ ...current, [active.sessionId]: [...(current[active.sessionId] ?? []), snapshot] }));
    setActiveHistorySnapshots((current) => ({ ...current, [active.sessionId]: snapshot.id }));
    setToast(`${snapshot.name} captured from ${activeLayer.name}.`);
  };

  const shellRegions = {
menus: (<nav
          ref={menuBarRef}
          className="menu-bar"
          aria-label="Application menu"
          onClick={(event) => {
            if (!(event.target instanceof Element)) return;
            const menu = event.target.closest('details');
            if (!(menu instanceof HTMLDetailsElement) || !event.currentTarget.contains(menu)) return;
            if (event.target.closest('summary')) {
              if (!menu.open) closeMenus(menu);
              return;
            }
            if (event.target.closest('button')) closeMenus();
          }}
        >
          <details><summary>File <ChevronDown size={11} /></summary><div className="menu-popover">
            <button type="button" onClick={() => setNewDialog(true)}><AppWindow size={14} />New document <kbd>⌘N</kbd></button>
            <button type="button" onClick={() => fileInput.current?.click()}><FolderOpen size={14} />Open… <kbd>⌘O</kbd></button>
            {actionIsOffered('place-image') ? <button type="button" onClick={() => placeImageInput.current?.click()}><FileImage size={14} />Place image…</button> : null}
            <button type="button" onClick={() => fontInput.current?.click()}><Type size={14} />Install fonts…</button>
            <span className="menu-separator" />
            <button type="button" onClick={() => void save()}><Save size={14} />Save Maipo project <kbd>⌘S</kbd></button>
            <button type="button" onClick={() => void save(true)}><Save size={14} />Save Maipo project as… <kbd>⇧⌘S</kbd></button>
            <button type="button" onClick={() => void exportPublic('json')}><Download size={14} />Export public scene JSON{hasNativePortableText(document.root) ? ' (portable draft)' : ''}</button>
            <button type="button" onClick={() => void exportPublic('package')}><Download size={14} />Export public Maipo package{hasNativePortableText(document.root) ? ' (portable draft)' : ''}</button>
            {actionIsOffered('png') ? <button type="button" onClick={() => void exportImage('png')}><Download size={14} />Export PNG</button> : null}
            {actionIsOffered('jpeg') ? <button type="button" onClick={() => void exportImage('jpeg')}><Download size={14} />Export JPEG</button> : null}
            {actionIsOffered('batch-process') ? <button type="button" onClick={() => setBatchProcessDialog(true)}>Batch Processing / Image Processor…</button> : null}
            <button type="button" onClick={() => setDataDrivenDialog(true)}>Variables &amp; Data-Driven Graphics…</button>
          </div></details>
          <details><summary>Edit <ChevronDown size={11} /></summary><div className="menu-popover">
            <button type="button" disabled={!historyState.canUndo} onClick={() => history.undo(document)}><Undo2 size={14} />Undo {historyState.undoLabel}<kbd>⌘Z</kbd></button>
            <button type="button" disabled={!historyState.canRedo} onClick={() => history.redo(document)}><Redo2 size={14} />Redo {historyState.redoLabel}<kbd>⇧⌘Z</kbd></button>
            <span className="menu-separator" />
            {actionIsOffered('edit-portable-text') ? <button type="button" disabled={selectedLayer?.kind !== 'text' || selectedLayer.text.schemaVersion !== 2 || selectedLayer.locked} onClick={() => { if (selectedLayer?.kind === 'text') setPortableCanvasEdit({ session: active, layer: selectedLayer, point: null }); }}>Edit portable text on canvas…</button> : null}
            {actionIsOffered('new-portable-text') ? <button type="button" onClick={() => setPortableTextSession(active)}>New portable text…</button> : null}
            <button type="button" onClick={duplicateLayer}><Layers3 size={14} />Duplicate layer<kbd>⌘J</kbd></button>
            <button type="button" disabled={document.activeLayerIds.length === 0} onClick={copyActiveLayer}>Copy layer<kbd>⌘C</kbd></button>
            <button type="button" disabled={!layerClipboard} onClick={() => void pasteLayer()}>Paste layer{layerClipboard ? ` “${layerClipboard.layer.name}”` : ''}<kbd>⌘V</kbd></button>
            <span className="menu-separator" />
            <button type="button" disabled={document.activeLayerIds.length === 0 || document.activeLayerIds.some((id) => isSmartObject(findLayer(document.root, id)))} onClick={convertToSmartObject}>Convert to Smart Object</button>
            <button type="button" disabled={!isSmartObject(activeLayer)} onClick={editSmartObjectContents}>Edit Smart Object contents</button>
            {actionIsOffered('rasterize-smart-object') ? <button type="button" disabled={!isSmartObject(activeLayer)} onClick={rasterizeSmartObject}>Rasterize Smart Object</button> : null}
            {actionIsOffered('fill-foreground') ? <button type="button" disabled={activeLayer?.kind !== 'raster' || Boolean(maskEditingLayerId)} onClick={fillForeground}>Fill selection with foreground</button> : null}
            {actionIsOffered('content-aware-fill') ? <button type="button" disabled={activeLayer?.kind !== 'raster' || document.selection.regions.length === 0 || document.selection.inverted} onClick={contentAwareFill}>Content-aware fill</button> : null}
            <button type="button" onClick={() => setToolPresetsDialog(true)}>Tool Presets…</button>
            <button type="button" onClick={() => setShortcutDialog(true)}>Keyboard shortcuts…</button>
          </div></details>
          <details><summary>Image <ChevronDown size={11} /></summary><div className="menu-popover">
            <button type="button" onClick={() => setGeometryDialog('image')}>Resize image…</button>
            <button type="button" onClick={() => setGeometryDialog('canvas')}>Resize canvas…</button>
            <button type="button" disabled={!hasSelection(document.selection) || !selectionBounds(document.selection, { x: 0, y: 0, width: document.width, height: document.height })} onClick={cropToSelection}>Crop to selection</button>
            {actionIsOffered('invert-layer-colors') ? <button type="button" onClick={invertActiveLayer}>Invert layer colors</button> : null}
            {actionIsOffered('black-white-adjustment') ? <button type="button" onClick={addBlackWhiteAdjustment}>Add Black &amp; White Adjustment Layer</button> : null}
            {actionIsOffered('image-trace') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={traceActiveLayer}>Image Trace (B&amp;W)</button> : null}
            {actionIsOffered('colour-trace') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => { closeMenus(); setColourTraceDialog(true); }}>Colour Trace…</button> : null}
            {actionIsOffered('liquify') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setDeformDialog('liquify')}>Liquify…</button> : null}
            {actionIsOffered('puppet-warp') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setDeformDialog('puppet')}>Puppet Warp…</button> : null}
            {actionIsOffered('warp-perspective') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setDeformDialog('warp-perspective')}>Free Transform · Warp / Perspective…</button> : null}
            {actionIsOffered('apply-image') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setApplyImageDialog(true)}>Apply Image / Calculations…</button> : null}
            <button type="button" onClick={() => setVanishingPointDialog(true)}>Perspective Grid / Vanishing Point…</button>
            {actionIsOffered('high-pass') ? <button type="button" disabled={activeLayer?.kind !== 'raster' && !isSmartObject(activeLayer)} onClick={() => setHighPassDialog(true)}>High Pass…</button> : null}
            {actionIsOffered('displace') ? <button type="button" disabled={activeLayer?.kind !== 'raster' && !isSmartObject(activeLayer)} onClick={() => setDisplaceDialog(true)}>Displace…</button> : null}
            {actionIsOffered('match-color') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setMatchColorDialog(true)}>Match Color…</button> : null}
            {actionIsOffered('content-aware-scale') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setContentAwareScaleDialog(true)}>Content-Aware Scale…</button> : null}
            <span className="menu-separator" />
            <button type="button" onClick={() => runCommand(active, 'document.rotate', { direction: 'clockwise' })}><RotateCw size={14} />Rotate 90° clockwise</button>
            <button type="button" onClick={() => runCommand(active, 'document.rotate', { direction: 'counterclockwise' })}><RotateCcw size={14} />Rotate 90° counterclockwise</button>
            <button type="button" onClick={() => runCommand(active, 'document.rotate', { direction: 'half-turn' })}>Rotate 180°</button>
            <button type="button" onClick={() => runCommand(active, 'document.flip', { axis: 'horizontal' })}>Flip horizontally</button>
            <button type="button" onClick={() => runCommand(active, 'document.flip', { axis: 'vertical' })}>Flip vertically</button>
          </div></details>
          <details><summary>Select <ChevronDown size={11} /></summary><div className="menu-popover">
            {actionIsOffered('select-all') ? <button type="button" onClick={() => history.execute(document, SelectionCommand.all())}>Select all pixels<kbd>⌘A</kbd></button> : null}
            {actionIsOffered('clear-selection') ? <button type="button" onClick={() => history.execute(document, SelectionCommand.clear())}>Clear pixel selection<kbd>⌘D</kbd></button> : null}
            {actionIsOffered('invert-selection') ? <button type="button" onClick={() => history.execute(document, SelectionCommand.invert())}>Invert pixel selection<kbd>⇧⌘I</kbd></button> : null}
            {actionIsOffered('refine-selection') ? <button type="button" disabled={!hasSelection(document.selection)} onClick={() => setRefineSelectionSource(structuredClone(document.selection))}>Refine Edge / Select and Mask…</button> : null}
            {actionIsOffered('color-range') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setColorRangeDialog(true)}>Color Range…</button> : null}
            {actionIsOffered('luminosity-masks') ? <button type="button" disabled={activeLayer?.kind !== 'raster'} onClick={() => setLuminosityMasksDialog(true)}>Luminosity Masks…</button> : null}
            {mediaMode === 'all' ? <span className="menu-separator" /> : null}
            {actionIsOffered('select-all-objects') ? <button type="button" disabled={!activeVectorObjects} onClick={() => { closeMenus(); applyObjectSelection(selectAllObjects(activeVectorObjects ?? [])); }}>Select all objects</button> : null}
            {actionIsOffered('inverse-objects') ? <button type="button" disabled={!activeVectorObjects || objectSelectionIds.length === 0} onClick={() => { closeMenus(); applyObjectSelection(invertObjectSelection(activeVectorObjects ?? [], objectSelectionIds)); }}>Inverse objects</button> : null}
            {actionIsOffered('select-same-fill') ? <button type="button" disabled={!activeVectorObjects || objectSelectionIds.length === 0} onClick={() => { closeMenus(); selectSameColour(); }}>Same fill colour</button> : null}
            {actionIsOffered('grow-object-selection') ? <button type="button" disabled={!activeVectorObjects || objectSelectionIds.length === 0} onClick={() => { closeMenus(); growObjectSelection(); }}>Grow to touching objects</button> : null}
            {actionIsOffered('select-touching-regions') ? <button type="button" disabled={!traceTopology || objectSelectionIds.length === 0} title={traceTopology ? 'Regions that share a boundary in the original trace' : 'Only traced artwork records exact contact'} onClick={() => { closeMenus(); selectTouchingObjects(); }}>Select regions that touch</button> : null}
            {mediaMode !== 'raster' ? <span className="menu-separator" /> : null}
            {actionIsOffered('group-objects') ? <button type="button" disabled={!activeVectorObjects || objectSelectionIds.length < 2} onClick={() => { closeMenus(); groupObjects(); }}>Group objects<kbd>⌘G</kbd></button> : null}
            {actionIsOffered('ungroup-objects') ? <button type="button" disabled={!activeVectorObjects || !groupedSelection} onClick={() => { closeMenus(); ungroupObjects(); }}>Ungroup<kbd>⇧⌘G</kbd></button> : null}
          </div></details>
          <details><summary>View <ChevronDown size={11} /></summary><div className="menu-popover view-menu">
            <button type="button" onClick={() => setShowRulers((value) => !value)}>{showRulers ? <Check size={14} /> : <span />}Rulers</button>
            <button type="button" onClick={() => setShowGrid((value) => !value)}>{showGrid ? <Check size={14} /> : <span />}Grid</button>
            <button type="button" onClick={() => setShowGuides((value) => !value)}>{showGuides ? <Check size={14} /> : <span />}Guides</button>
            <button type="button" onClick={() => setSnapEnabled((value) => !value)}>{snapEnabled ? <Check size={14} /> : <span />}Snap to guides and grid</button>
            <span className="menu-separator" />
            <button type="button" onClick={() => setGuideDialog(true)}>Add guide…</button>
            <button type="button" onClick={() => setGuideLayoutDialog(true)}>New Guide Layout…</button>
            <button type="button" disabled={(document.guides?.length ?? 0) === 0} onClick={() => runCommand(active, 'guides.clear', {})}>Clear guides</button>
          </div></details>
        </nav>),
actions: (<div className="header-actions">
          <CadenciaBridge open={openCadenciaFile} session={active} />
          {recoveries.length ? <button type="button" className="recovery-button" onClick={() => setRecoveryDialog(true)}><RotateCcw size={14} /> {recoveries.length} recoverable</button> : null}
          <div className="media-mode" role="radiogroup" aria-label="Workspace media mode" onKeyDown={onMediaModeKeyDown}>
            {MEDIA_MODES.map((candidate) => <button
              key={candidate}
              type="button"
              role="radio"
              aria-checked={mediaMode === candidate}
              tabIndex={mediaMode === candidate ? 0 : -1}
              data-mode={candidate}
              className={mediaMode === candidate ? 'active' : ''}
              title={candidate === 'all'
                ? 'Show every tool and command'
                : `Show only ${candidate} tools and commands. This filters the interface; it never changes the document.`}
              onClick={() => switchMediaMode(candidate)}
            >{modeLabel(candidate)}</button>)}
          </div>
          <button type="button" className="appearance-button" onClick={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')} aria-label={`Use ${theme === 'dark' ? 'light' : 'dark'} theme`} title={`Use ${theme === 'dark' ? 'light' : 'dark'} theme`}>{theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}</button>
          <button type="button" className="appearance-button" onClick={() => setDensity((value) => value === 'compact' ? 'comfortable' : 'compact')} aria-label={`Use ${density === 'compact' ? 'comfortable' : 'compact'} density`} title={`Use ${density === 'compact' ? 'comfortable' : 'compact'} density`}><Rows3 size={14} /></button>
          <button type="button" className="command-button" aria-label="Commands" title="Commands" onClick={() => setPaletteOpen(true)}><Command size={14} /><span>Commands</span><kbd>⌘K</kbd></button>
          {installable ? <button type="button" className="install-button" onClick={() => void promptInstall()} title="Install Maipo Studio for faster offline launches"><DownloadCloud size={14} /><span>Install app</span></button> : null}
          <CloudWorkspace
            active={active}
            onOpenDocument={(nextDocument) => addSession(nextDocument).sessionId}
            onToast={setToast}
          />
        </div>),
tools: (<aside className="tool-rail" aria-label="Editing tools">
          {TOOL_GROUPS.map((group) => {
            const inGroup = visibleTools.filter((item) => item.group === group);
            if (inGroup.length === 0) return null;
            return <div className="tool-rail-group" role="group" aria-label={group} key={group}>
              {inGroup.map((item) => {
                const Icon = item.icon;
                const key = toolShortcuts[item.id];
                const unavailable = quickMaskActive && (item.id === 'brush' || item.id === 'eraser') ? null : toolUnavailableReason(item.id, activeLayer, maskEditingLayerId);
                return <button type="button" key={item.id} className={tool === item.id ? 'active' : ''} disabled={Boolean(unavailable)} onClick={() => setTool(item.id)} aria-label={`${item.label} (${key})${unavailable ? `: ${unavailable}` : ''}`} title={unavailable ?? `${item.label} (${key})`}><Icon size={18} /><span>{key}</span></button>;
              })}
            </div>;
          })}
          {mediaMode !== 'vector' ? <div className="tool-rail-group" role="group" aria-label="Mask">
            <button type="button" className={quickMaskActive ? 'active' : ''} aria-pressed={quickMaskActive} aria-label="Quick Mask mode (Q)" title="Quick Mask mode (Q)" onClick={() => setQuickMaskSessions((current) => ({ ...current, [active.sessionId]: !current[active.sessionId] }))}><SquareDashedMousePointer size={18} /><span>Q</span></button>
          </div> : null}
          {hiddenToolCount > 0 ? <span className="tool-rail-hidden" title={`${hiddenToolCount} tools are hidden by the ${modeLabel(mediaMode)} workspace. Their shortcuts stay bound but will not activate them.`}>{hiddenToolCount} hidden</span> : null}
          <div className="tool-rail-spacer" />
          <div className="color-wells" title="Foreground and background colors">
            <label className="foreground-well" style={{ background: foregroundHex }}><input type="color" value={foregroundHex} onChange={(event) => applyForeground(event.target.value)} /></label>
            <label className="background-well" style={{ background: backgroundColor }}><input type="color" value={backgroundColor} onChange={(event) => setBackgroundColor(event.target.value)} /></label>
          </div>
        </aside>),
settings: (<OptionsBar tool={tool} brush={brush} setBrush={setBrush} quickSelectionTolerance={quickSelectionTolerance} setQuickSelectionTolerance={setQuickSelectionTolerance} exposure={exposure} setExposure={setExposure} exposureRange={exposureRange} setExposureRange={setExposureRange} protectTones={protectTones} setProtectTones={setProtectTones} selectionMode={selectionMode} setSelectionMode={setSelectionMode} shapeKind={shapeKind} setShapeKind={setShapeKind} gridSettings={gridSettings} setGridSettings={setGridSettings} scaleStrokesEffects={scaleStrokesEffects} setScaleStrokesEffects={(value) => { setScaleStrokesEffects(value); window.localStorage.setItem('maipo-scale-strokes-effects', String(value)); }} vectorDrawMode={vectorDrawMode} setVectorDrawMode={setVectorDrawMode} document={document} history={history} vectorObjectSelection={vectorObjectSelection} editingTarget={editingTarget} toolBlockedReason={toolBlockedReason} perspectiveClone={perspectiveClone} setPerspectiveClone={setPerspectiveClone} onManageToolPresets={() => setToolPresetsDialog(true)} />),
canvas: (<section className={`workspace-shell${document.extensions['maipo:image-source-edit'] ? ' source-edit-workspace' : ''}`}>
          {document.extensions['maipo:image-source-edit'] ? <div className="source-edit-banner" role="region" aria-label="Source pixel editing">
            <div><strong>Source pixels</strong><p>{sourceEdits.current.has(active.sessionId) ? 'Apply flattens these edits to PNG and preserves the original image frame.' : 'This source edit is no longer linked. Export PNG, then use Replace image in the original document.'}</p></div>
            {sourceEdits.current.has(active.sessionId) ? <button disabled={Boolean(progress) || active.currentRevision === 0 || history.hasLiveCommand} onClick={() => { void applySourceEdit(); }}>Apply to original</button> : null}
            <button disabled={Boolean(progress)} onClick={() => finishSourceEdit(active, sourceEdits.current.get(active.sessionId))}>Discard source edits</button>
          </div> : null}
          <CanvasWorkspace
            onEditPortableText={(layer, point) => setPortableCanvasEdit({ session: active, layer, point })}
            key={active.sessionId}
            document={document}
            history={history}
            tool={tool}
            temporaryHand={temporaryHand}
            brush={brush}
            quickSelectionTolerance={quickSelectionTolerance}
            exposureSettings={{ exposure, range: exposureRange, protectTones }}
            notes={documentNotes(document)}
            onCreateNote={addNoteAt}
            historyBrushSource={historyBrushSource}
            quickMaskActive={quickMaskActive}
            channelView={channelView}
            alphaChannelSelection={alphaChannelSelection}
            artboards={canvasRegions}
            perspectivePlanes={activePerspectivePlane(document) ? [activePerspectivePlane(document)!] : []}
            perspectiveClone={perspectiveClone}
            backgroundColor={backgroundColor}
            maskEditingLayerId={maskEditingLayerId}
            vectorObjectSelection={vectorObjectSelection}
            groupEntry={groupEntry}
            onGroupEntry={setGroupEntry}
            onVectorObjectSelection={setVectorObjectSelection}
            isolationScope={isolationScope}
            onIsolationScope={setIsolationScope}
            toolBlockedReason={toolBlockedReason}
            onDirectTextEditingChange={setDirectTextEditingLayerId}
            showGrid={showGrid}
            showGuides={showGuides}
            showRulers={showRulers}
            snapEnabled={snapEnabled}
            scaleStrokesEffects={scaleStrokesEffects}
            selectionMode={selectionMode}
            shapeKind={shapeKind}
            gridSettings={gridSettings}
            vectorDrawMode={vectorDrawMode}
            revision={revision}
            onPointerReadout={setReadout}
            onViewport={setViewport}
            onRenderStats={setRenderStats}
            viewAction={viewAction}
          />
        </section>),
resizer: (<button
          type="button"
          className="dock-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel dock"
          aria-valuenow={dockWidth}
          aria-valuemin={DOCK_MIN_WIDTH}
          aria-valuemax={DOCK_MAX_WIDTH}
          aria-valuetext={`${dockWidth} pixels`}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            dockDragging.current = true;
          }}
          onPointerMove={(event) => {
            if (!dockDragging.current) return;
            const next = Math.max(DOCK_MIN_WIDTH, Math.min(DOCK_MAX_WIDTH, Math.round(window.innerWidth - event.clientX)));
            dockWidthRef.current = next;
            setDockWidth(next);
          }}
          onPointerUp={(event) => endDockDrag(event.currentTarget, event.pointerId)}
          // Without these a pointercancel — an OS interrupt mid-drag — leaves the
          // drag flag set, and a later hover over the 5px strip resizes with no
          // button held.
          onPointerCancel={(event) => endDockDrag(event.currentTarget, event.pointerId)}
          onLostPointerCapture={(event) => endDockDrag(event.currentTarget, event.pointerId)}
          onKeyDown={(event) => {
            // WAI-ARIA: Home is the minimum and End the maximum. These were the
            // wrong way round, contradicting the aria-valuemin/max on the same
            // element.
            const next = event.key === 'ArrowLeft' ? dockWidth + 16
              : event.key === 'ArrowRight' ? dockWidth - 16
              : event.key === 'Home' ? DOCK_MIN_WIDTH
              : event.key === 'End' ? DOCK_MAX_WIDTH
              : null;
            if (next === null) return;
            event.preventDefault();
            const clamped = Math.max(DOCK_MIN_WIDTH, Math.min(DOCK_MAX_WIDTH, next));
            setDockWidth(clamped);
            window.localStorage.setItem('maipo-dock-width', String(clamped));
            setMediaAnnouncement(`Panel dock ${clamped} pixels.`);
          }}
        />),
panels: (<aside className="right-dock">
          <section className={`color-panel ${colorOpen ? '' : 'collapsed'}`}>
            <button type="button" className="panel-title color-title-toggle" aria-expanded={colorOpen} onClick={() => setColorOpen((open) => { window.localStorage.setItem('maipo-color-open', String(!open)); return !open; })}>
              <span>{colorOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}<Palette size={14} /> Color</span><code>{foregroundHex.toUpperCase()}</code>
            </button>
            {colorOpen ? <>
              <div className="color-control-row"><input type="color" value={foregroundHex} aria-label="Foreground color" onChange={(event) => applyForeground(event.target.value)} /><div><strong>Foreground</strong><span>RGB {brush.color[0]} · {brush.color[1]} · {brush.color[2]}</span></div></div>
              <div className="palette-heading"><span>Recent</span><span>Saved palette</span><button type="button" title="Save foreground to palette" onClick={() => setPaletteColors((current) => current.includes(foregroundHex) ? current : [...current, foregroundHex].slice(-24))}>+</button></div>
              <div className="recent-swatches" aria-label="Recent colors">
                {recentColors.map((color) => <button key={color} type="button" aria-label={`Use recent color ${color}`} style={{ background: color }} onClick={() => applyForeground(color)} />)}
              </div>
              <div className="saved-swatches" aria-label="Saved palette">
                {paletteColors.map((color) => <button key={color} type="button" aria-label={`Use saved color ${color}`} title={`${color} · Shift-click to remove`} style={{ background: color }} onClick={(event) => event.shiftKey ? setPaletteColors((current) => current.filter((item) => item !== color)) : applyForeground(color)} />)}
              </div>
              <GlobalSwatchesPanel document={document} session={active} foreground={foregroundHex} selection={vectorObjectSelection} onSelectSwatch={selectBySwatch} />
            </> : null}
          </section>
          <div className="panel-families" role="tablist" aria-label="Panel families" onKeyDown={familyRoving.onKeyDown}>
            {visibleFamilies.map((family, index) => <button
              key={family.id}
              type="button"
              role="tab"
              aria-selected={dockFamily === family.id}
              tabIndex={index === familyRoving.focusIndex ? 0 : -1}
              className={dockFamily === family.id ? 'active' : ''}
              title={family.title}
              onClick={() => selectDockFamily(family.id)}
            >{family.label}</button>)}
          </div>
          <div className="panel-tabs" role="tablist" aria-label="Panels" onKeyDown={panelRoving.onKeyDown}>
            {familyPanels.map((panel, index) => <button
              key={panel.id}
              type="button"
              role="tab"
              aria-selected={rightTab === panel.id}
              id={`${dockId}-tab-${panel.id}`}
              aria-controls={`${dockId}-panel`}
              tabIndex={index === panelRoving.focusIndex ? 0 : -1}
              className={rightTab === panel.id ? 'active' : ''}
              {...(panel.addon ? { title: panel.addon } : {})}
              onClick={() => setRightTab(panel.id as typeof rightTab)}
            >{panel.label}</button>)}
            <button type="button" className="panel-browser-open" title="All panels" aria-haspopup="dialog" onClick={() => setPanelBrowser(true)}>ALL {allDockPanels.length}</button>
          </div>
          <div className="panel-body" role="tabpanel" id={`${dockId}-panel`} aria-labelledby={`${dockId}-tab-${rightTab}`} tabIndex={0}>
            {rightTab === 'layers' ? <LayerPanel onCreatePortableText={() => setPortableTextSession(active)} onDuplicateLayer={duplicateLayer} onEditImagePixels={layerId => { void editImagePixels(layerId); }} session={active} document={document} history={history} mediaMode={mediaMode} onSwitchMediaMode={switchMediaMode} maskEditingLayerId={maskEditingLayerId} onMaskEditingLayerId={setMaskEditingLayerId} vectorObjectSelection={vectorObjectSelection} onVectorObjectSelection={setVectorObjectSelection} isolationScope={isolationScope} onIsolationScope={setIsolationScope} /> : null}
            {rightTab === 'fill' ? <FillPanel document={document} history={history} session={active} mediaMode={mediaMode} onExport={(format) => void exportImage(format)} onExportAll={sessions.length > 1 ? () => void exportAllImages() : undefined} sessionCount={sessions.length} /> : null}
            {rightTab === 'history' ? <div className="history-panel">
              <div className="history-head"><span>{historyState.timeline.length} reversible steps</span>{mediaMode !== 'vector' ? <button type="button" onClick={takeHistorySnapshot}>＋ Snapshot</button> : null}<button type="button" disabled={!historyState.canUndo} onClick={() => history.undo(document)}><Undo2 size={14} /> Undo</button></div>
              {mediaMode !== 'vector' ? <div className="history-snapshots">{sessionSnapshots.length ? sessionSnapshots.map((snapshot) => <div className={`history-snapshot ${snapshot.id === activeSnapshot?.id ? 'active' : ''}`} key={snapshot.id}><button type="button" onClick={() => setActiveHistorySnapshots((current) => ({ ...current, [active.sessionId]: snapshot.id }))}><CircleDot size={13} /><span>{snapshot.name}<small>{snapshot.layerName}</small></span></button><button type="button" aria-label={`Delete ${snapshot.name}`} onClick={() => setHistorySnapshots((current) => ({ ...current, [active.sessionId]: (current[active.sessionId] ?? []).filter((item) => item.id !== snapshot.id) }))}>×</button></div>) : <p className="history-empty">Capture the active raster layer, then choose History Brush (Y) to paint that state back.</p>}</div> : null}
              <div className="history-list"><button type="button" className={`history-entry origin ${historyState.cursor === 0 ? 'current' : ''}`} onClick={() => history.jumpTo(document, 0)}><CircleDot size={13} /> Open document</button>{historyState.timeline.map((entry, index) => {
                const command = historyCommands[index];
                const actor = command ? active.ledger.actorOf(command) : USER_ACTOR;
                const by = actorDisplayName(actor);
                const revertible = index < historyState.cursor && offersRevert(command);
                return <div className={`history-row ${revertible ? 'revertible' : ''}`} data-actor={actor.kind} key={`${entry}:${index}`}>
                  <button type="button" className={`history-entry ${index + 1 === historyState.cursor ? 'current' : ''} ${index + 1 > historyState.cursor ? 'future' : ''}`} aria-label={`Step ${index + 1}: ${entry}, by ${by}`} onClick={() => history.jumpTo(document, index + 1)}><span>{index + 1}</span>{withoutActorPrefix(entry, actor)}<small className="history-actor" aria-hidden="true">{by}</small></button>
                  {revertible ? <button type="button" className="history-revert" aria-label={`Revert “${entry}”`} title="Apply a new step that undoes only this change" onClick={() => revertHistoryEntry(command)}>Revert</button> : null}
                </div>;
              })}</div>
            </div> : null}
            {rightTab === 'actions' ? <div className="actions-panel">
              <div className={`actions-head ${recordingActionName ? 'recording' : ''}`}><div><strong>{recordingActionName ? `● Recording “${recordingActionName}”` : 'Actions Palette'}</strong><span>{recordingActionName ? `${recordedStepCount} committed step${recordedStepCount === 1 ? '' : 's'}` : 'Declarative, persisted, stop-on-failure replay'}</span></div><button type="button" className={recordingActionName ? 'quiet-danger' : 'primary'} onClick={recordingActionName ? stopActionRecording : startActionRecording}>{recordingActionName ? 'Stop' : 'Record'}</button></div>
              <div className="actions-list">{visibleRecordedActions.length ? visibleRecordedActions.map((action) => <div className="action-row" key={action.id}><div><strong>{action.name}</strong><span>{action.steps.length} steps · {action.steps.filter((step) => step.type === 'marker').length ? 'partial' : 'replayable'}</span></div><button type="button" title="Play in current document" disabled={Boolean(progress)} onClick={() => { void playRecordedAction(action); }}>▶</button><button type="button" title="Play in every open document" disabled={Boolean(progress)} onClick={() => { void playRecordedAction(action, true); }}>All</button><button type="button" title="Rename" onClick={() => {
                const name = window.prompt('Action name', action.name)?.trim();
                if (name && name !== action.name) runCommand(active, 'actions.rename', { action: action.id, name });
              }}>Rename</button><button type="button" className="quiet-danger" title="Delete" onClick={() => runCommand(active, 'actions.delete', { action: action.id })}>×</button></div>) : <p className="history-empty">{documentActions(document).length ? `${documentActions(document).length} recorded action${documentActions(document).length === 1 ? ' is' : 's are'} hidden by the ${modeLabel(mediaMode)} workspace because at least one step is not offered here.` : 'Press Record, perform normal editor commands, then Stop. Supported edits replay with recorded settings; unsupported edits remain visible markers and stop safely.'}</p>}</div>
            </div> : null}
            {rightTab === 'channels' ? <ChannelsPanel document={document} session={active} view={channelView} onView={(view) => setChannelViews((current) => ({ ...current, [active.sessionId]: view }))} /> : null}
            {rightTab === 'export' ? <ExportPanel document={document} session={active} onExportAssets={exportMarkedAssets} onExportArtboards={exportArtboards} onExportSlices={exportSlices} /> : null}
            {rightTab === 'notes' ? <NotesPanel document={document} session={active} /> : null}
            {rightTab === 'document' ? <div className="document-info-panel">
              <div className="document-preview-icon"><FileImage size={26} /></div>
              <h3>{document.name}</h3><p>{document.width.toLocaleString()} × {document.height.toLocaleString()} px</p>
              <dl><div><dt>Working color</dt><dd>sRGB / 8-bit</dd></div><div><dt>Resolution</dt><dd>{document.resolution.x} ppi</dd></div><div><dt>Pixel aspect</dt><dd>{document.pixelAspectRatio}:1</dd></div><div><dt>Source</dt><dd>{document.source?.kind ?? 'new'}</dd></div><div><dt>Raster memory</dt><dd>{(memory / 1024 / 1024).toFixed(1)} MiB</dd></div><div><dt>Renderer</dt><dd>Canvas 2D</dd></div></dl>
              {document.importReport ? <button type="button" className="report-link" onClick={() => setReport(document.importReport ?? null)}><Info size={14} /> View import report</button> : null}
              <div className="color-warning"><Info size={15} /><p>Profile metadata is retained. This release edits and exports in 8-bit sRGB.</p></div>
              <LayerCompsPanel document={document} session={active} />
            </div> : null}
            {rightTab === 'histogram' ? <HistogramPanel document={document} revision={revision} /> : null}
            {rightTab === 'agent' ? <AgentAccessPanel sessionId={active.sessionId} documentName={document.name} /> : null}
            {visibleAddonPanels.map(({ addon, panel }) => rightTab === `addon:${addon.id}:${panel.id}`
              ? <AddonPanelHost key={`${addon.id}:${panel.id}`} addon={addon} panel={panel} session={active} document={document} history={history} onNotify={setToast} />
              : null)}
          </div>
        </aside>),
status: (<footer className="status-bar">
        <div className="sr-only" role="status" aria-live="polite">{mediaAnnouncement}</div>
        <div><span className="status-ready"><i /> Ready</span>{isolationScope ? <span className="mask-status">Isolation Mode</span> : null}{maskEditingLayerId ? <span className="mask-status">Mask target</span> : null}{quickMaskActive ? <span className="mask-status">Quick Mask · Brush reveals / Eraser hides</span> : null}<span>{readout.inside ? `X ${readout.x}  Y ${readout.y}` : 'Outside canvas'}</span><span>{document.width} × {document.height} px</span></div>
        <div><span>{editingTarget.label} · {editingTarget.detail}</span><span className="status-dev">{renderStats.renderedTiles} tiles · {renderStats.elapsedMs.toFixed(1)} ms · {Math.round(renderStats.cacheHitRate * 100)}% cache{renderStats.inputToPresentMs === undefined ? '' : ` · ${renderStats.inputToPresentMs.toFixed(1)} ms input→present`}</span><span className="status-dev">{renderStats.canvasAllocations} canvases · history {(historyState.retainedBytes / 1024 / 1024).toFixed(1)} MiB</span><span className="status-dev">Workers {workerStats.activeTasks} active / {workerStats.cancelledTasks} cancelled</span><span className="status-dev">Recovery {active.recovery.status}{active.recovery.durationMs === undefined ? '' : ` · ${active.recovery.durationMs.toFixed(0)} ms`}</span><span>{Math.round(viewport.zoom * 100)}%</span></div>
      </footer>),
};
  const shellHost = createEditorHost(sessions, activeId);

  return (
    <div
      className="maipo-app mp-root maipo-next"
      data-cadencia={(window.parent !== window && new URLSearchParams(window.location.hash.slice(1)).has('cadencia')) || undefined}
      data-theme={theme}
      data-density={density}
      onDragEnter={(event) => { if (event.dataTransfer.types.includes('Files')) setDragActive(true); }}
      onDragOver={(event) => {
        event.preventDefault();
        setDragSide(event.clientX < window.innerWidth / 2 ? 'layer' : 'open');
      }}
      onDragLeave={(event) => { if (event.currentTarget === event.target) setDragActive(false); }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        void dropFiles([...event.dataTransfer.files], event.clientX < window.innerWidth / 2 ? 'layer' : 'open');
      }}
    >
      <Suspense fallback={<div role="status">Opening Maipo…</div>}><EditorShell
        host={shellHost}
        regions={shellRegions}
        toolLabel={tools.find((item) => item.id === tool)?.label ?? tool}
        target={editingTarget.label + ' · ' + editingTarget.detail}
        zoom={viewport.zoom}
        dockWidth={dockWidth}
        canUndo={historyState.canUndo} canRedo={historyState.canRedo}
        onUndo={() => history.undo(document)} onRedo={() => history.redo(document)}
        onSelectDocument={setActiveId}
        onCloseDocument={closeDocument}
        onNewDocument={() => setNewDialog(true)}
        onDropLayer={(layerId, sessionId) => void copyLayerToSession(layerId, sessionId)}
        onZoom={(action) => setViewAction((value) => ({ id: value.id + 1, action }))}
      /></Suspense>

      {panelBrowser ? <div className="panel-browser-scrim" role="presentation" onClick={() => setPanelBrowser(false)}>
          <div className="panel-browser" role="dialog" aria-modal="true" aria-label="All panels" onClick={(event) => event.stopPropagation()}>
            <header><strong>All panels</strong><span>{allDockPanels.length} registered</span><button type="button" aria-label="Close" onClick={() => setPanelBrowser(false)}>×</button></header>
            <div className="panel-browser-body">
              {PANEL_FAMILIES.map((family) => {
                const inFamily = allDockPanels.filter((panel) => panel.family === family.id);
                if (inFamily.length === 0) return null;
                return <section key={family.id}>
                  <h4>{family.label}</h4>
                  {inFamily.map((panel) => {
                    const hidden = !dockPanelOffered(panel);
                    return <button
                      key={panel.id}
                      type="button"
                      className={hidden ? 'hidden-here' : ''}
                      onClick={() => {
                        if (hidden) return;
                        setDockFamily(panel.family);
                        setRightTab(panel.id as typeof rightTab);
                        setPanelBrowser(false);
                      }}
                      disabled={hidden}
                    >
                      <span>{panel.label}</span>
                      {panel.addon ? <em>add-on</em> : null}
                      {hidden ? <em className="hidden-tag">hidden in {modeLabel(mediaMode)}</em> : null}
                    </button>;
                  })}
                </section>;
              })}
            </div>
          </div>
        </div> : null}
      <input ref={fileInput} type="file" hidden multiple accept=".maipo,.json,.psd,.svg,image/svg+xml,image/png,image/jpeg,image/webp,image/gif,image/bmp" onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ''; void openFiles(files); }} />
      <input ref={placeImageInput} type="file" hidden aria-label="Place image file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void placeImage(file); }} />
      <input ref={fontInput} type="file" hidden multiple accept=".ttf,.otf,.woff,.woff2" onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ''; void installFonts(files); }} />
      {dragActive ? <div className="drop-overlay split">
        <div className={`drop-zone ${dragSide === 'layer' ? 'active' : ''}`}>
          <Upload size={26} />
          <strong>Add to “{document.name}”</strong>
          <span>Images land as layers in the open document · fonts install</span>
        </div>
        <div className={`drop-zone ${dragSide === 'open' ? 'active' : ''}`}>
          <FileImage size={26} />
          <strong>Open as new document</strong>
          <span>Images, layered PSDs, Maipo projects and public scenes</span>
        </div>
      </div> : null}
      {progress ? <div className="progress-overlay" role="status"><div><span className="progress-spinner" /><strong>{progress.message}</strong><progress max="1" value={progress.value} /><button type="button" onClick={() => progress.controller.abort()}>Cancel</button></div></div> : null}
      {toast ? <div className="toast" role="status">{toast}<button type="button" onClick={() => setToast(null)} aria-label="Dismiss"><X size={13} /></button></div> : null}
      <AgentDialogs />
      {/* Lazy dialogs resolve their chunk before mounting; the fallback is
          intentionally empty so no placeholder flashes over the canvas. */}
      <Suspense fallback={null}>
        {newDialog ? <NewDocumentDialog onClose={() => setNewDialog(false)} onCreate={(next) => { addSession(next); setNewDialog(false); }} /> : null}
        {portableCanvasEdit?.session === active ? <PortableTextCanvasEditor session={active} layer={portableCanvasEdit.layer} initialPoint={portableCanvasEdit.point} recovered={portableCanvasEdit.recovered} onClose={(message) => { setPortableCanvasEdit(null); if (message) setToast(message); void listAllRecoveries().then(setRecoveries).catch(() => undefined); }} /> : null}
        {portableTextSession === active ? <CreatePortableTextDialog session={active} onClose={() => setPortableTextSession(null)} onCreated={() => { setPortableTextSession(null); setVectorObjectSelection(null); setIsolationScope(null); setRightTab('layers'); if (mediaMode === 'raster') switchMediaMode('all'); }} /> : null}
        {geometryDialog ? <GeometryDialog mode={geometryDialog} document={document} session={active} onClose={() => setGeometryDialog(null)} /> : null}
        {deformDialog && activeLayer?.kind === 'raster' ? <DeformDialog mode={deformDialog} document={document} session={active} layer={activeLayer} onClose={() => setDeformDialog(null)} /> : null}
        {applyImageDialog && activeLayer?.kind === 'raster' ? <ApplyImageDialog document={document} session={active} activeLayer={activeLayer} onClose={() => setApplyImageDialog(false)} /> : null}
        {colorRangeDialog && activeLayer?.kind === 'raster' ? <ColorRangeDialog document={document} history={history} layer={activeLayer} initialColor={foregroundHex} onClose={() => setColorRangeDialog(false)} /> : null}
        {colourTraceDialog && activeLayer?.kind === 'raster' ? <ColourTraceDialog document={document} history={history} layer={activeLayer} onClose={() => setColourTraceDialog(false)} /> : null}
        {vanishingPointDialog ? <VanishingPointDialog document={document} session={active} activeLayer={activeLayer?.kind === 'raster' ? activeLayer : null} onClose={() => setVanishingPointDialog(false)} /> : null}
        {batchProcessDialog ? <BatchProcessDialog actions={visibleRecordedActions} onClose={() => setBatchProcessDialog(false)} /> : null}
        {dataDrivenDialog ? <DataDrivenDialog document={document} mediaMode={mediaMode} onClose={() => setDataDrivenDialog(false)} /> : null}
        {highPassDialog && activeLayer && (activeLayer.kind === 'raster' || isSmartObject(activeLayer)) ? <HighPassDialog document={document} session={active} target={activeLayer} onClose={() => setHighPassDialog(false)} /> : null}
        {displaceDialog && activeLayer && (activeLayer.kind === 'raster' || isSmartObject(activeLayer)) ? <DisplaceDialog document={document} history={history} target={activeLayer} onClose={() => setDisplaceDialog(false)} /> : null}
        {matchColorDialog && activeLayer?.kind === 'raster' ? <MatchColorDialog document={document} session={active} target={activeLayer} onClose={() => setMatchColorDialog(false)} /> : null}
        {luminosityMasksDialog && activeLayer?.kind === 'raster' ? <LuminosityMasksDialog document={document} history={history} layer={activeLayer} onClose={() => setLuminosityMasksDialog(false)} /> : null}
        {contentAwareScaleDialog && activeLayer?.kind === 'raster' ? <ContentAwareScaleDialog document={document} session={active} layer={activeLayer} onClose={() => setContentAwareScaleDialog(false)} /> : null}
        {guideDialog ? <GuideDialog document={document} session={active} onClose={() => setGuideDialog(false)} /> : null}
        {guideLayoutDialog ? <GuideLayoutDialog document={document} session={active} onClose={() => setGuideLayoutDialog(false)} /> : null}
        {shortcutDialog ? <ShortcutDialog shortcuts={toolShortcuts} defaults={defaultToolShortcuts} onSave={setToolShortcuts} onClose={() => setShortcutDialog(false)} /> : null}
        {toolPresetsDialog ? <ToolPresetsDialog document={document} session={active} visibleToolIds={visibleTools.map((item) => item.id)} onApply={(preset) => {
          const presetTool = tools.find((item) => item.id === preset.tool);
          if (!presetTool || !visibleIn(presetTool.media, mediaMode)) {
            setToast(`${presetTool?.label ?? 'That preset tool'} is hidden by the ${modeLabel(mediaMode)} workspace. The preset was not applied.`);
            return false;
          }
          setTool(preset.tool);
          if (preset.settings.brush) setBrush(structuredClone(preset.settings.brush));
          if (preset.settings.selectionMode) setSelectionMode(preset.settings.selectionMode);
          if (preset.settings.shapeKind) setShapeKind(preset.settings.shapeKind);
          if (preset.settings.gridSettings) setGridSettings(structuredClone(preset.settings.gridSettings));
          if (preset.settings.quickSelectionTolerance !== undefined) setQuickSelectionTolerance(preset.settings.quickSelectionTolerance);
          if (preset.settings.perspectiveClone !== undefined) setPerspectiveClone(preset.settings.perspectiveClone);
          if (preset.settings.exposure) {
            setExposure(preset.settings.exposure.amount);
            setExposureRange(preset.settings.exposure.range);
            setProtectTones(preset.settings.exposure.protectTones);
          }
          return true;
        }} onClose={() => setToolPresetsDialog(false)} /> : null}
        {refineSelectionSource ? <RefineSelectionDialog document={document} history={history} original={refineSelectionSource} activeLayerId={activeLayer?.kind === 'raster' ? activeLayer.id : null} onClose={() => setRefineSelectionSource(null)} /> : null}
        {report ? <ReportDialog report={report} onClose={() => setReport(null)} /> : null}
        {recoveryDialog ? <RecoveryDialog recoveries={recoveries} onClose={() => setRecoveryDialog(false)} onRestore={(item) => {
          if (isTextDraftRecovery(item)) {
            void loadTextDraftRecovery(item.key).then(({ document: next, layerId, value }) => {
              const layer = findLayer(next.root, layerId);
              if (layer?.kind !== 'text') throw new Error('Recovered text layer is unavailable.');
              const restored = addSession(next);
              setPortableCanvasEdit({ session: restored, layer, point: null, recovered: { key: item.key, value } });
              setRecoveryDialog(false); setToast('Recovered unapplied text. Review it, then Apply or Cancel.');
            }).catch((reason: unknown) => setToast(reason instanceof Error ? reason.message : 'Text draft recovery failed.'));
          } else {
            void loadRecovery(item.key).then((next) => { addSession(next); setRecoveryDialog(false); setToast(`Recovered ${next.name}.`); }).catch((reason: unknown) => setToast(reason instanceof Error ? reason.message : 'Recovery failed.'));
          }
        }} onDelete={(item) => { void (isTextDraftRecovery(item) ? deleteTextDraftRecovery(item.key) : deleteRecovery(item.sessionId)).then(() => listAllRecoveries()).then(setRecoveries).catch(() => setToast('Recovery could not be deleted.')); }} /> : null}
        {paletteOpen ? <CommandPalette actions={visibleActions} onClose={() => setPaletteOpen(false)} /> : null}
      </Suspense>
    </div>
  );
}
